/* eslint-disable */
/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never;
    };
import type { TypedDocumentNode as DocumentNode } from "@graphql-typed-document-node/core";
export type AddPersonaQuestionInput = {
  expectedVersion: number;
  fromTemplateId?: string | number | null | undefined;
  idempotencyKey?: string | null | undefined;
  note?: string | null | undefined;
  personaId: string | number;
  question: string;
};

export type AddQuestionTemplateInput = {
  idempotencyKey?: string | null | undefined;
  question: string;
  /**
   * 担当させるルール。**画面はルールの一覧から選ばせる**（文字列の一致で
   * 結ばない）。存在しない id を渡すと `BAD_USER_INPUT`。
   */
  ruleId?: string | number | null | undefined;
  scope?: string | null | undefined;
};

/** 採否。人が決めるもので、機械は決めない */
export type Adoption = "ADOPTED" | "ON_HOLD";

export type AiClientKind = "CHATGPT" | "CLAUDE" | "GEMINI" | "OTHER";

export type AiConnectionStatus = "ACTIVE" | "SUSPENDED";

export type AnswerableBasis =
  /** 判定で答えられる問いが 1 件以上ある商品（材料は問わない） */
  | "ANY_COVERED"
  /** 生成済みの FAQ で答えられる問いが 1 件以上ある商品 */
  | "FAQ_COVERED";

/**
 * 答えられる商品の絞り込み。**集計（ペルソナ別の充足）で判定した商品だけが対象で、
 * 未集計の商品は含まれない**。`personaId` を渡すと、そのペルソナの値だけで判定する
 */
export type AnswerableProductFilter = {
  basis?: AnswerableBasis;
  personaId?: string | number | null | undefined;
};

/**
 * キーで呼べる範囲。**実在するものは 1 つだけ**である。
 *
 * 設計の SDL は自由な文字列（`scope: String!`）だったが、受け取れる値が
 * 1 つしか無いところへ任意の文字列を通すと、honor できない値が保存できてしまう。
 */
export type ApiKeyScope =
  /** 配信中の答えを読む。読み取りだけで、書き込む口は持たない */
  "DELIVERY_READ";

/**
 * 音声の状態。
 *
 * **「文字起こしが 0 行」と「音が無い」を同じ表示にしないため**に持つ。
 * 設計の `SPEECH / BGM_ONLY / NO_AUDIO / EFFECTIVELY_SILENT` は、BGM と発話を
 * 分けて判定する実体が無いので置いていない。
 */
export type AudioState =
  /** 音声をまだ見ていない（読解していない動画） */
  | "NOT_CHECKED"
  /** 音声から文字が 1 つも取れなかった */
  | "SILENT"
  /** 文字起こしが取れた */
  | "TRANSCRIBED";

/**
 * 監査イベントの種別。
 *
 * **サーバーの `AdminAuditEventKind` と 1 対 1**（片方にしか無い種別があると、
 * 台帳に入っている行が読み出せずに落ちるか、黙って捨てられる）。
 * 突き合わせは `admin-store-repository` の固定テストが構造で見ている。
 */
export type AuditEventKind =
  | "AI_CONNECTION_ISSUED"
  | "AI_CONNECTION_STATUS_CHANGED"
  | "AI_OPERATION_CHANGED"
  | "AI_SHARE_SCOPE_CHANGED"
  | "ALLOWED_ORIGINS_CHANGED"
  | "API_KEY_CREATED"
  | "API_KEY_REVOKED"
  | "ATTRIBUTE_ADDED"
  | "ATTRIBUTE_LEVEL_CHANGED"
  /** ブランドとして意識したい文脈の変更（W31・DEC-7） */
  | "BRAND_CONTEXT_CHANGED"
  | "COLLECT_TASK_CHANGED"
  | "DELEGATION_ENDED"
  | "DELEGATION_STARTED"
  | "EXTERNAL_CONNECTED"
  | "EXTERNAL_DISCONNECTED"
  /**
   * 重なっていた答えを 1 本に片づけた（決定 D4）。
   *
   * **設計の一覧には無い加算**。答えが消える唯一の操作なので、ほかの種別へ
   * 寄せると台帳の上で「何が消えたか」を追えなくなる。
   */
  | "FAQ_DUPLICATES_MERGED"
  | "INVITATION_REVOKED"
  /**
   * 走っているジョブを取り消した（FR26）。
   *
   * **設計の一覧には無い加算**。`JOB_FINISHED` へ寄せると、自分で終わったのか
   * 人が止めたのかが台帳の上で見分けられない。どこまで止まったか
   * （`cancelEffect`）も、この行の値として残す。
   */
  | "JOB_CANCELLED"
  | "JOB_ENQUEUED"
  | "JOB_FINISHED"
  /**
   * 失敗ぶんを再実行した（FR26.5）。
   *
   * **設計の一覧には無い加算**。`JOB_ENQUEUED` へ寄せると、新しく起こした実行と
   * やり直しが見分けられず、**費用が 2 回出た理由**を後から説明できない。
   */
  | "JOB_RETRIED"
  | "KNOWLEDGE_SHEET_UPDATED"
  | "MEMBER_INVITED"
  /**
   * 招待を受け取って、この会社の所属ができた（**設計の一覧には無い加算**）。
   * `MEMBER_INVITED` は招待を出した側の操作なので、受け取った事実を
   * そこへ寄せると「誰がいつ入ったか」が監査から読めなくなる。
   * actor は参加した本人である。
   */
  | "MEMBER_JOINED"
  | "MEMBER_REMOVED"
  | "MEMBER_ROLE_CHANGED"
  /**
   * パスキーの counter が逆行した（資格情報の複製が疑われる）。
   * `PASSKEY_SIGN_IN_REJECTED` とは別に、調査用に残す。資格情報は自動では消えない。
   */
  | "PASSKEY_COUNTER_REGRESSED"
  /**
   * 利用者本人がパスキーを登録した（資源は `SELF`）。
   *
   * パスキーはテナントに属さないので、**本人が所属する全テナント**の監査へ
   * 同じ行が入る。`before` / `after` に載るのは資格情報 ID の先頭 8 文字と名前だけ。
   */
  | "PASSKEY_REGISTERED"
  /** 利用者本人がパスキーを削除した。その利用者のログイン中のセッションも失効する */
  | "PASSKEY_REMOVED"
  /** 利用者本人がパスキーの名前を変えた（`PASSKEY_REGISTERED` と同じく全所属テナントへ入る） */
  | "PASSKEY_RENAMED"
  /**
   * パスキーでサインインした（段階 2）。`PASSWORD_SIGN_IN` と同じく、認証の成否が
   * 決まった後に 1 回だけ書かれ、本人が所属する全テナントの監査へ入る。
   * `after` に載るのは方法と、資格情報 ID の先頭 8 文字と名前だけ。
   */
  | "PASSKEY_SIGN_IN"
  /**
   * パスキーでのサインインに失敗した。応答から**利用者が読めたときだけ**書かれる。
   * 登録が無い・署名が違う・チャレンジの期限切れ・counter の逆行を区別しない。
   */
  | "PASSKEY_SIGN_IN_REJECTED"
  /**
   * パスワードでサインインした。認証の成否が決まった後に 1 回だけ書かれ、
   * 本人が所属する全テナントの監査へ入る。
   */
  | "PASSWORD_SIGN_IN"
  /**
   * パスワードでのサインインに失敗した。**利用者が特定できたときだけ**書かれる
   * （存在しないログイン ID での失敗はどのテナントにも属さない）。
   */
  | "PASSWORD_SIGN_IN_REJECTED"
  /** ペルソナの作成・更新・削除（W29・DEC-7） */
  | "PERSONA_CHANGED"
  /** ペルソナの観点の変更（W30・DEC-7） */
  | "PERSONA_QUESTION_CHANGED"
  | "PRODUCT_ARTIFACTS_IMPORTED"
  /**
   * 属性の機械生成の 1 段目(商品画像の取り込み)を起こした。
   *
   * **オペレーターが起こす操作**だが、記録は起こされた会社の監査へ残す
   * （その会社の商品が処理されるので、その会社の履歴として読めなければならない）。
   */
  | "PRODUCT_SEMANTICS_FILL_STARTED"
  /** 属性の機械生成の 2 段目（DLT パイプライン）を起こした */
  | "PRODUCT_SEMANTICS_PIPELINE_STARTED"
  | "PUBLISH_REFRESH_STARTED"
  | "PUBLISH_TARGET_CHANGED"
  | "QUESTION_TEMPLATE_ADDED"
  | "QUESTION_TEMPLATE_TOGGLED"
  | "REGULATION_CHANGED"
  | "RULE_DRAFT_UPDATED"
  | "RULE_PUBLISHED_VERSION_CHANGED"
  | "RULE_STATE_CHANGED"
  | "RULE_VERSION_SAVED"
  /** run / stop の権限設定の変更（W23・DEC-4） */
  | "RUN_STOP_POLICY_CHANGED"
  | "SEGMENT_PRODUCT_CONFIRMED"
  | "SEGMENT_PRODUCT_UNCONFIRMED"
  | "SELLING_ANGLE_ADOPTION_CHANGED"
  /** 新規動画の自動処理の設定変更（W25・DEC-5） */
  | "VIDEO_META_AUTO_INGEST_CHANGED"
  /** 既存動画の一括処理の開始（W24・DEC-5） */
  | "VIDEO_META_BATCH_STARTED"
  /** 書き戻しを反映した */
  | "WRITEBACK_APPLIED"
  /** 書き戻しの提案を承認した */
  | "WRITEBACK_APPROVED"
  /** 自動書き戻しの許可範囲の変更（W26・DEC-9） */
  | "WRITEBACK_POLICY_CHANGED"
  /** 書き戻しの提案が立った（DEC-9） */
  | "WRITEBACK_PROPOSED"
  /**
   * 書き戻しの提案を却下した。
   *
   * **設計の一覧には無い加算**。承認と対になる操作で、`WRITEBACK_APPROVED` へ
   * 寄せると台帳の上で承認と却下が見分けられなくなる。
   */
  | "WRITEBACK_REJECTED"
  /** 書き戻しを元に戻した（W28・DEC-9） */
  | "WRITEBACK_REVERTED";

export type AuditFilter = {
  /** 代行で行われた操作だけに絞る */
  delegatedOnly?: boolean | null | undefined;
  kind?: AuditEventKind | null | undefined;
  resource?: PolicyResource | null | undefined;
  resourceId?: string | number | null | undefined;
  since?: string | null | undefined;
  until?: string | null | undefined;
};

/** 取り消したいジョブ。**どこまで止まるかは返ってくる `job.cancelEffect` が言う** */
export type CancelJobInput = {
  idempotencyKey?: string | null | undefined;
  jobId: string | number;
};

/** 商品候補を、動画の**どこから**読み取ったか */
export type CandidateBasis =
  /** 画面に出ていた文字（テロップ・パッケージの表記など）から */
  | "ON_SCREEN_TEXT"
  /** 話していた言葉から */
  | "SPOKEN"
  /** 映っているものの見た目から */
  | "VISUAL";

/** 参考の初期値（DERIVED）から複製してブランド定義のペルソナを作る（FR28.5） */
export type ClonePersonaInput = {
  idempotencyKey?: string | null | undefined;
  name: string;
  sourcePersonaId: string | number;
};

export type ConfirmSegmentProductInput = {
  /**
   * いまの確定の版。**まだ確定していなければ 0**。
   * 食い違えば `CONFLICT`（誰かが先に触った）。
   */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  /** 確定する商品。この環境の商品マスターにあるものだけ */
  productId: string | number;
  segmentIndex: number;
  videoId: string | number;
};

/** 取り込み元がいまつながっているか。**失敗は表せない**(実行記録がまだ無い) */
export type ConnectionState = "CONNECTED" | "NOT_CONNECTED";

export type CoverageBand = "HIGH" | "LOW" | "MIDDLE";

export type CreateApiKeyInput = {
  idempotencyKey?: string | null | undefined;
  label: string;
  scope: ApiKeyScope;
};

export type CreatePersonaInput = {
  attributes?: Array<string> | null | undefined;
  barriers?: Array<string> | null | undefined;
  description: string;
  idempotencyKey?: string | null | undefined;
  name: string;
  questions?: Array<string> | null | undefined;
};

/**
 * **設計の SDL には無い加算的な入力**。ルールを作る口が無いとテナントは
 * 1 本もルールを持てない（設計はルールが既にある前提で書かれている）。
 */
export type CreateRuleInput = {
  answerMaxChars?: number | null | undefined;
  banned?: Array<string> | null | undefined;
  description: string;
  idempotencyKey?: string | null | undefined;
  maxPerProduct?: number | null | undefined;
  minEvidenceCount?: number | null | undefined;
  name: string;
  /**
   * 最初の版の中身。省略すると、いま生成器が使っている既定の指示が版 1 として
   * 入る（既定を写しておかないと、何で作られた答えなのかが後から辿れない）。
   */
  promptBody?: string | null | undefined;
  questions?: Array<string> | null | undefined;
  scope?: string | null | undefined;
};

export type DelegationReason =
  "CUSTOMER_REQUEST" | "PERIODIC_REVIEW" | "SETUP_SUPPORT" | "TROUBLESHOOTING";

export type DeleteBrandContextInput = {
  brandContextId: string | number;
  idempotencyKey?: string | null | undefined;
  /** どのペルソナに紐づいていたか。null はテナント全体 */
  personaId?: string | number | null | undefined;
};

export type DeletePersonaInput = {
  /** 食い違えば CONFLICT。読んだ版のままであることを条件にする（FR18） */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  personaId: string | number;
};

export type DryRunRuleInput = {
  idempotencyKey?: string | null | undefined;
  /** 商品を名指しする場合。省略すると母数の先頭から sampleSize 件 */
  productIds?: Array<string | number> | null | undefined;
  /**
   * 試す版。**設計の SDL には無い加算的なフィールド**。省略すると下書きを試す
   * （下書きが無ければ配信中の版）。保存済みの別の版を指定することもできる。
   */
  promptVersion?: string | null | undefined;
  ruleId: string | number;
  /** 名指ししないときに試す件数。費用が出るので上限がある */
  sampleSize?: number | null | undefined;
};

/**
 * 処理する動画を名指しして始める入力（FR27.2 の「この動画だけ」）。
 *
 * 一覧から 1 本ずつ試すときと、**この環境に置いた確認用の動画**を通すときに使う。
 */
export type EnqueueVideoMetaJobInput = {
  idempotencyKey?: string | null | undefined;
  /** 1〜50 件。0 件なら `BAD_USER_INPUT` */
  targets: Array<VideoMetaTargetInput>;
};

export type EvidenceType =
  | "INFERRED"
  | "MASTER"
  | "REVIEW_AGG"
  | "REVIEW_PROFILE_AGG"
  | "REVIEW_TEXT"
  | "UGC";

/**
 * **設計の SDL には無い加算的な語彙。** 生成した答えを検証で捨てた理由。
 *
 * 「試しても何も出ない」ときに、画面が理由を言えるようにするために足した。
 * dev の実測（2026-09-19）では、捨てた件数の合計だけが台帳にあり、0 件の理由は
 * ワーカーのログにしか無かった。合計が 0 のまま 0 件になる経路
 * （応答が読めなかった）まであり、画面からは何も言えなかった。
 */
export type FaqDropReason =
  /** 答えが字数の上限を超えた（切らずに捨てる） */
  | "ANSWER_TOO_LONG"
  /** ルールの禁止表現を含んでいた */
  | "BANNED_WORD"
  /** 同じ応答の中で問いが重複していた */
  | "DUPLICATE_QUESTION"
  /** 形が違う・問いや答えが空 */
  | "MALFORMED_ENTRY"
  /** 根拠が付いていなかった */
  | "NO_EVIDENCE"
  /** 1 商品あたりの上限を超えたぶん */
  | "OVER_LIMIT"
  /** 入力に実在しない項目を根拠に挙げていた */
  | "UNKNOWN_EVIDENCE"
  /**
   * 応答が JSON として読めなかった。**数えるのは応答の本数**で、Q&A の件数ではない
   * （何件入っていたかは分からないので droppedFaqs には足さない）
   */
  | "UNREADABLE_RESPONSE";

/**
 * FAQ 生成の対象の決め方。見積もりと開始で**同じ入力型**を使う
 * （別の型にすると、見積もった対象と開始する対象がずれても型が通る）。
 */
export type FaqGenerationTargetInput = {
  /** 母数。省略すると配信対象 */
  population?: PopulationId | null | undefined;
  /** 商品を名指しする場合。省略すると母数の全件（上限まで） */
  productIds?: Array<string | number> | null | undefined;
};

/** 登録の往復 2。認証器の応答を検証して保存する */
export type FinishPasskeyRegistrationInput = {
  /** 名前(1〜64 文字)。省略すると認証器の名前か「パスキー <登録日>」になる */
  label?: string | null | undefined;
  /** RegistrationResponseJSON の JSON 文字列 */
  responseJson: string;
  ticket: string;
};

export type IngestChannel =
  /** 外部 API */
  | "API"
  /** 定期のエクスポートを取り込む */
  | "EXPORT"
  /** 管理画面から受け取るファイル(レビューはこの形が正式な口) */
  | "FILE_HANDOFF"
  /** 商品フィード */
  | "PRODUCT_FEED";

export type IngestTier =
  /** 実験的コネクタ。既定の構成には入らない */
  | "EXPERIMENTAL"
  /** 後続。要望はあるが今回は外したもの */
  | "LATER"
  /** これだけで画面が成立する集合(10 月に必須のもの) */
  | "REQUIRED";

/**
 * メンバーの招待（W22・FR16）。
 *
 * 事業者の管理者だけが呼べる。サーバーが断るもの（画面の分岐にしない）:
 * **すでに所属している宛先**・**有効な招待がある宛先**（先に取り消す）・
 * `CS_DELEGATE` の指定・形になっていないメールアドレス。
 *
 * 招待は `MEMBER_INVITED` として監査に残るが、**宛先のメールアドレスと
 * トークンは監査に入らない**（監査の値は画面にそのまま出る）。
 */
export type InviteMemberInput = {
  email: string;
  idempotencyKey?: string | null | undefined;
  role: TenantRole;
};

export type IssueAiConnectionInput = {
  client: AiClientKind;
  department?: string | null | undefined;
  idempotencyKey?: string | null | undefined;
  memberId: string | number;
  memberName: string;
};

/**
 * 取り消しがどこまで効いたか。**設計の SDL には無い加算的な語彙。**
 *
 * 取り消しの口は 1 つだが、止められる範囲は実行の基盤ごとに違う。
 * 画面が「止まりました」と書けるのは `NOT_STARTED` のときだけで、
 * 残りは「取り消しを受け付けた・走っているものは最後まで走る」と出す。
 */
export type JobCancelEffect =
  /**
   * まだ始まっていないうちに止めた。**何も走らない。**
   *
   * 待ち行列の伝票は残るが、台帳が `QUEUED` でなくなるとワーカーは
   * 取りかかれずに降りる（`markRunning` の条件つき更新）。
   */
  | "NOT_STARTED"
  /**
   * 走っている途中で止めたが、**始まっている段は最後まで走る。**
   *
   * 動画メタ化は実行そのものを止められる（止めれば、動画を切り出している
   * コンテナも一緒に止まる）。ただし**すでに始まっている文字起こしは止まらず、
   * その本数ぶんの費用は出る**。結果は使われずに捨てられる。
   */
  | "RUNNING_STAGE_CONTINUES"
  /**
   * 走っている途中で止めた。**次の区切りで降りる。**
   *
   * ワーカーは一定の件数ごとに台帳へ進捗を書き、`RUNNING` でなくなっていれば
   * そこで降りる。取りかかっている 1 件は最後まで走る。
   */
  | "STOPS_AT_NEXT_CHECKPOINT";

export type JobKind =
  | "FAQ_GENERATION"
  /**
   * 商品ページ本文と JSON-LD の組み立て（J2）。
   *
   * **LLM を呼ばない**ので月次予算を消費しない。材料は商品マスターの列と、
   * すでに作ってある**配信中の答え**だけで、文章を新しく作る余地が無いためである
   * （作るとしたら商品説明だが、その口は `Product.description` で別に用意する）。
   * 配信はしない — 出来上がりは `Product.page` で見るだけである。
   */
  | "PAGE_GENERATION"
  /** ペルソナ × 観点のカバレッジ再計算（J9）。LLM を呼ばないので予算を消費しない */
  | "PERSONA_COVERAGE"
  /**
   * ルールの試し生成（dry run）。**`Faq` を 1 件も書かない**。
   * 生成そのものは FAQ 生成と同じなので、**同じ月次予算を消費する**
   * （試すぶんだけ本番の生成が減る、という形にしてある）。
   */
  | "RULE_DRY_RUN"
  /**
   * 動画メタ化（J4）。デコード（ECS）→ 文字起こし（Amazon Transcribe）→
   * 読解（Bedrock）→ 保存を、1 本のステートマシンで通す。
   *
   * **自分自身の器で月次予算を取る**。動画 1 本につきモデルを 1 回呼ぶが、
   * そのほかに文字起こしとデコードの費用が乗るので、FAQ 生成と同じ器に入れると
   * 1 本あたりの実費が 3 倍以上違うものを同じ残量で数えることになる。
   */
  | "VIDEO_META";

export type JobState =
  | "CANCELLED"
  | "FAILED"
  | "QUEUED"
  | "RUNNING"
  | "SUCCEEDED"
  | "SUCCEEDED_WITH_ERRORS";

/**
 * 商品候補との結びつきの状態。
 *
 * **人が確定したかどうか**が軸である（`CONFIRMED` は人の操作の結果で、
 * 読解がひとりでに立てることはない）。
 */
export type LinkState =
  /** 人が商品を確定した区間がある */
  | "CONFIRMED"
  /** 読解が商品候補を出した。まだ人は確定していない */
  | "SUGGESTED"
  /** 商品候補が 1 つも出ていない */
  | "UNLINKED";

export type MarkNotificationsReadInput = {
  all?: boolean | null | undefined;
  notificationIds?: Array<string | number> | null | undefined;
};

export type MediaKind = "IMAGE" | "NONE" | "VIDEO";

/**
 * メンバーの状態（W22・FR16）。
 *
 * `members` は参加済みの人だけ、`invitations` は招待中の人だけを返すので、
 * この enum が値として画面に出るのは `Invitation.status` だけである。
 * `EXPIRED` は**保存されない**（期限を過ぎた招待を読み出すときに導出する）。
 */
export type MemberStatus =
  /** 期限を過ぎた招待。もう受け取れない（取り消すか、出し直す）。 */
  | "EXPIRED"
  /** 招待を出してあり、まだ受け取られていない。 */
  | "INVITED"
  /** 参加済み。`members` の行がこの状態である。 */
  | "JOINED";

/**
 * 片づけの実行。
 *
 * `acknowledgedRemovedCount` は `faqDuplicates` が返した `removedCount` を
 * そのまま渡す。**必須** — 見ずに消せないようにするためで、実行時の件数と
 * 食い違えば `CONFLICT` で断る（`startFaqGeneration` と同じ形）。
 */
export type MergeDuplicateFaqsInput = {
  acknowledgedRemovedCount: number;
  idempotencyKey?: string | null | undefined;
  productId: string | number;
};

export type NotificationKind =
  | "BUDGET_EXHAUSTED"
  | "DELEGATION_ENDED"
  | "DELEGATION_STARTED"
  | "JOB_FAILED"
  | "JOB_SUCCEEDED"
  | "JOB_SUCCEEDED_WITH_ERRORS";

/** 端末の種類。同期されるパスキーか、その端末だけのものか */
export type PasskeyDeviceType =
  /** 同期済み(同じアカウントの別の端末でも使える) */
  | "MULTI_DEVICE"
  /** その端末だけ(同期されない) */
  | "SINGLE_DEVICE";

export type PersonaOrigin =
  /** ブランドが定義したもの。カバレッジの主役 */
  | "BRAND_DEFINED"
  /**
   * レビュアー属性の集計から起こした人物像。**実測から起こした参考値**で、
   * ブランド定義のものと同じ一覧に混ぜない。複製して BRAND_DEFINED を作る起点にする。
   */
  | "DERIVED";

export type PipelineState = "OK" | "STUCK" | "UNKNOWN";

export type PolicyAction = "ADMINISTER" | "READ" | "RUN_STOP" | "WRITE";

/**
 * ポリシーの対象資源。設計の SDL は全資源を列挙するが、ここには
 * **この縦切りで実際に @policy から参照するものだけ**を置く。
 * enum 値の追加は加算的なので、後続 PR がそれぞれ必要なものを足す。
 */
export type PolicyResource =
  /**
   * 担当者ごとの AI との連携(MCP の接続キー)。一覧と繋ぎかたの読み取り、
   * 発行と停止・再開の書き込みを当てる。監査の行が指す資源としても返る。
   *
   * **発行だけは代行でできない**。`admin-policy.ts` の `DELEGATION_FORBIDDEN` が
   * `resourceId = ISSUE` のときだけ落とす(停止は事故を止める操作なので代行でも通す)。
   */
  | "AI_CONNECTION"
  /** 顧客の開発者向けキー。監査の行が指す資源として返る(発行の口はまだ無い) */
  | "API_KEY"
  /**
   * 監査ログそのもの。**読み取りしか無い**(`admin-policy.ts` の
   * `READ_ONLY_RESOURCES`)。台帳は本体の書き込みと同じトランザクションでしか
   * 増えず、画面から書き換える口は作らない。
   */
  | "AUDIT"
  /** ホームの集計(contextCoverage)。読み取りしか無い資源で、書き込み系は通らない */
  | "COVERAGE"
  /**
   * CS 代行そのもの。**いま入っている代行を終わらせる**操作にだけ当てる
   * (`endDelegation`)。発行の側は会社の管理なので `TENANT`(オペレーター専用)で、
   * こちらは顧客向けの面からも押せる必要がある — 代行の帯の「終了」がそれである。
   *
   * 代行中の文脈(`CS_DELEGATE`)でも `WRITE` が通る。自分がいま入っている代行を
   * 閉じる操作を、代行から取り上げない(事故を止める側の操作)。
   */
  | "DELEGATION"
  /**
   * 取り込み元(データの取り込み)。いまは**参照しかしない**
   * (`admin-policy.ts` の `READ_ONLY_RESOURCES`)。
   *
   * つなぐ・切る・取り込みを起動する、といった書き込みの口は載せていない。
   * 保存しても外側の実体(Databricks のジョブ設定・外部 API の資格情報)へ
   * 届かないためである。届く経路ができたら、この資源を読み取り専用の集合から外す。
   */
  | "EXTERNAL_CONNECTION"
  /**
   * ジョブ台帳(起動・進捗の参照)。FAQ 生成の起動には **`RUN_STOP`** を当てる。
   * 費用の出る実行なので、役割の表に加えてテナント設定(`runStopPolicy`)の判断を
   * 通す必要があるためである(rules.md §2.3 / ADR-16)。
   */
  | "JOB"
  /**
   * 商品知識(属性値と出所・人手の確定と修正・版)。読み取りと、人手の確定・修正の
   * 書き込みに当てる。生成のやり直しなど**費用の出る実行**はこの資源では通さない
   * (ジョブの起動は `JOB` の `RUN_STOP`)。
   */
  | "KNOWLEDGE"
  /**
   * メンバーと招待。**代行では触れない**(`admin-policy.ts` の
   * `DELEGATION_FORBIDDEN`)。この面を読み書きする operation はまだ無いが、
   * 監査の行が指す資源として返るので語彙に入れてある。
   */
  | "MEMBER"
  | "PERSONA"
  | "PRODUCT"
  /**
   * 配信(公開の対象・範囲・配信ログ)。**公開の切り替えには `RUN_STOP`** を当てる。
   * ルールの run / stop と同じ「公開に直結する操作」なので、役割の表に加えて
   * テナント設定(`runStopPolicy`)を通す。**切り替えても配信は走らない**が、
   * 走るようになった日に権限だけが緩いままになるのを避ける。
   */
  | "PUBLISH"
  /**
   * 想定質問のひな形(AI Question Map)。読み取りと、テナントが足す・止めるの
   * 書き込みに当てる。**数え直しの起動はこの資源では通さない** — 数え直しは
   * 既存の `recomputePersonaCoverage`(`JOB` の `RUN_STOP`)に相乗りしている。
   */
  | "QUESTION"
  /**
   * レギュレーションブック(ブランドの言いかた)。**どのデータ源からも導けない**
   * ので、人が書いて登録したものだけが載る。読み取りと登録・削除に当てる。
   */
  | "REGULATION"
  /**
   * ルール（生成の設定とプロンプトの版）。読み書きは `READ` / `WRITE` だが、
   * **run / stop と配信中の版の入れ替えは `RUN_STOP`** を当てる。公開に直結する
   * 操作なので、役割の表に加えてテナント設定（`runStopPolicy`）を通す。
   */
  | "RULE"
  /**
   * 利用者本人の資源(パスキーなど)。テナントに属さない。トークンの本人だけが
   * 読み書きでき、代行中は通らない(`admin-policy.ts` の `decideSelf`)。
   * 役割の表は見ない。監査の行が指す資源としても返る。
   * 設計: docs/design/2026-09-23-passkey-authentication.md 7.1 節
   */
  | "SELF"
  /**
   * テナントそのもの(横断の管理)。**オペレーター専用**で、顧客向けトークンは
   * どの操作でも弾かれる(admin-policy.ts の OPERATOR_ONLY_RESOURCES)。
   * 顧客が読む集計をここへ載せないこと — 画面が必ず FORBIDDEN になる。
   */
  | "TENANT"
  /**
   * 動画メタ。いまは**参照しかしない**（`admin-policy.ts` の `READ_ONLY_RESOURCES`）。
   *
   * 一括処理の開始・1 本ずつの投入・新規の自動処理の設定といった書き込みの口は
   * 載せていない。処理の実体（デコードと読解）がまだ無く、保存しても何も起きない
   * ためである。処理が入ったら、この資源を読み取り専用の集合から外す。
   */
  | "VIDEO_META"
  /**
   * 統制語彙。正本は ETL の契約で、管理画面からは**参照しかしない**
   * (`admin-policy.ts` の `READ_ONLY_RESOURCES`)。
   */
  | "VOCABULARY"
  /**
   * 外部への書き戻し。許可範囲の設定は `ADMINISTER`、個々の提案の起票・承認・
   * 却下は `WRITE` を当てる。**代行では設定変更だけが止まる**
   * (`admin-policy.ts` の `DELEGATION_FORBIDDEN`。承認は事故を止める側の操作なので
   * 代行でも通す)。
   */
  | "WRITEBACK";

export type PopulationId =
  "ALL" | "BY_CATEGORY" | "DELIVERED" | "TOP_SALES" | "WITH_UGC";

export type ProductFilter = {
  /**
   * 答えられる商品に絞る。ペルソナ別の充足の集計が商品ごとに残した数で判定する。
   * **集計（ペルソナ別の充足）で判定した商品だけが対象で、未集計の商品は含まれない**
   * （1 回の集計は母数の商品名順の先頭 1000 件まで・全ペルソナを数え直した実行の
   * 結果だけを使う）。判定には `products` の `population` と同じ母数の集計を使う
   */
  answerable?: AnswerableProductFilter | null | undefined;
  category?: string | null | undefined;
  coverageBand?: CoverageBand | null | undefined;
  hasDescription?: boolean | null | undefined;
  hasImage?: boolean | null | undefined;
  publish?: PublishState | null | undefined;
  query?: string | null | undefined;
  /**
   * 売り方の提案の候補に絞る: 属性の機械生成の対象になれる商品（UGC 50 件以上の上位 200 —
   * etl の承認コホートと同じ定義）と、属性が生成済みの商品。true のときだけ効く
   */
  sellingCandidates?: boolean | null | undefined;
};

export type ProductOrder =
  "COVERAGE_ASC" | "COVERAGE_DESC" | "FAQ_DELIVERED_DESC" | "NAME_ASC";

/**
 * **設計の SDL には無い加算的な input。** 提案を作る口が 1 つも無いと、
 * 承認の一覧が「常に空の枠」になる。組み立て済みの商品ページ(`Product.page`)を
 * EC の商品詳細の原稿(`PRODUCT_COPY`)へ反映する提案として起票する。
 *
 * 先を引数で受けないのは、**何をするかが口の名前で決まる**ようにするためである
 * (何でも受ける汎用の口にすると、材料の無い先へも提案が作れてしまう)。
 */
export type ProposeProductPageWritebackInput = {
  idempotencyKey?: string | null | undefined;
  productId: string | number;
};

export type PublishState = "PUBLISHED" | "STOPPED" | "UNSET";

export type PublishTargetKind =
  /** 商品ページの本文 */
  | "BODY"
  /** 商品ページの JSON-LD */
  | "JSONLD"
  /** 生成 AI 向けの案内ファイル(llms.txt) */
  | "LLMS_TXT"
  /** サイトマップ */
  | "SITEMAP";

export type QuestionOrigin =
  /**
   * visumo が育てる共通のひな形。**いま実物が 1 件も無い**ので、この値を
   * 持つ行は返らない。共通ひな形の正本（どこに置き、誰が足すか）が決まるまで、
   * ダミーの行を作って「ある」ように見せることはしない。
   */
  | "SHARED_TEMPLATE"
  /** このテナントが足したひな形 */
  | "TENANT_ADDED";

export type RecomputePersonaCoverageInput = {
  idempotencyKey?: string | null | undefined;
  /** 対象のペルソナ。省略するとブランド定義のペルソナ全部 */
  personaIds?: Array<string | number> | null | undefined;
  /** 母数。省略すると配信対象 */
  population?: PopulationId | null | undefined;
};

/**
 * メンバーの除名（W22・FR16）。
 *
 * 利用者そのものは消さない（ほかの会社に所属しているかもしれない）。
 * 消えるのは**この会社の所属**だけである。
 */
export type RemoveMemberInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  memberId: string | number;
};

export type RemovePasskeyInput = {
  id: string | number;
};

export type RemovePersonaQuestionInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  personaId: string | number;
  questionId: string | number;
};

export type RenamePasskeyInput = {
  id: string | number;
  label: string;
};

/**
 * 失敗ぶんの再実行（FR26.5）。
 *
 * `targetIds` を省略すると、そのジョブの失敗ぶん全部が対象になる。
 */
export type RetryJobInput = {
  idempotencyKey?: string | null | undefined;
  jobId: string | number;
  /**
   * 再実行する対象を絞る場合。元のジョブの `failedTargets` に無い id を
   * 混ぜると `BAD_USER_INPUT`（**黙って落として成功を返さない**）。
   */
  targetIds?: Array<string | number> | null | undefined;
};

export type RevokeApiKeyInput = {
  /** 読んだときの `ApiKey.version`。食い違えば `CONFLICT` で断る */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  keyId: string | number;
};

/**
 * 招待の取り消し（W22・FR16）。
 *
 * 取り消すと一覧から消え、そのトークンでは受け取れなくなる。
 * 取り消しは `INVITATION_REVOKED` として監査に残る。
 */
export type RevokeInvitationInput = {
  idempotencyKey?: string | null | undefined;
  invitationId: string | number;
};

/**
 * 公開に直結する唯一のスイッチ（D-6 / INV-1）。`RUN` のルールが作った答えだけが
 * 配信の対象になる。切り替えは専用の mutation（`setRuleState`）だけが行い、
 * 押した人・時刻・版が監査に残る。
 */
export type RuleState = "RUN" | "STOP";

/**
 * run / stop を誰が押せるかのテナント設定（ADR-16 / DEC-4）。
 *
 * **設定が無いテナントは `ADMIN_AND_CS_ONLY` として扱う**（fail-closed / FR9.2.3）。
 * 「設定が無いから全員可」にしない。
 */
export type RunStopPolicyValue =
  /** 管理者と CS 代行のみ（既定） */
  | "ADMIN_AND_CS_ONLY"
  /** 編集者にも許す */
  | "EDITOR_ALLOWED";

export type SaveRuleVersionInput = {
  answerMaxChars?: number | null | undefined;
  banned?: Array<string> | null | undefined;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  maxPerProduct?: number | null | undefined;
  minEvidenceCount?: number | null | undefined;
  promptBody: string;
  promptVersion: string;
  ruleId: string | number;
  scope?: string | null | undefined;
};

/** 属性の必須度。いまはカテゴリ別の登録先が無いので参照のみ */
export type SchemaLevel = "OPTIONAL" | "REQUIRED" | "UNUSED";

export type SetAiConnectionStatusInput = {
  connectionId: string | number;
  idempotencyKey?: string | null | undefined;
  status: AiConnectionStatus;
};

export type SetAllowedOriginsInput = {
  /** 読んだときの `AllowedOrigins.version`。食い違えば `CONFLICT` で断る */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  origins: Array<string>;
};

/**
 * メンバーの役割の変更（W22・FR16）。
 *
 * `expectedVersion` は `members` が返した `version` をそのまま渡す。
 * 食い違えば `CONFLICT` で断る（**読まずに書く経路を作らない**）。
 * `CS_DELEGATE` は指定できない（代行は招待や設定で作る役割ではない）。
 */
export type SetMemberRoleInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  memberId: string | number;
  role: TenantRole;
};

export type SetNotificationPreferenceInput = {
  email: boolean;
  inApp: boolean;
};

export type SetPublishTargetInput = {
  enabled: boolean;
  idempotencyKey?: string | null | undefined;
  target: PublishTargetKind;
};

export type SetPublishedPromptVersionInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  promptVersion: string;
  ruleId: string | number;
};

/**
 * ひな形 1 件ぶんのカバレッジ。数字は集計ジョブ
 * （`recomputePersonaCoverage`）が書いたカウンタの読み出しである。
 */
export type SetQuestionTemplateEnabledInput = {
  enabled: boolean;
  idempotencyKey?: string | null | undefined;
  templateId: string | number;
};

export type SetRuleStateInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  ruleId: string | number;
  state: RuleState;
};

/**
 * run / stop の権限設定の変更（W23・DEC-4）。
 *
 * `expectedVersion` は `runStopPolicy` が返した `version` をそのまま渡す。
 * 食い違えば `CONFLICT` で断る（**読まずに書く経路を作らない**）。
 */
export type SetRunStopPolicyInput = {
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  value: RunStopPolicyValue;
};

export type SetSellingAngleAdoptionInput = {
  adoption: Adoption;
  angleKey: string;
  idempotencyKey?: string | null | undefined;
  productId: string | number;
};

/**
 * 自動処理の設定を書き換える入力。
 *
 * `expectedVersion` は `videoMetaAutoIngest` が返した `version` をそのまま渡す。
 * 食い違えば `CONFLICT` で断る（**読まずに書く経路を作らない**）。
 */
export type SetVideoMetaAutoIngestInput = {
  enabled: boolean;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  /** 1 以上。null は「本数の上限を置かない」 */
  monthlyLimit?: number | null | undefined;
};

export type SetWritebackPolicyInput = {
  /** **全量**(差分ではない)。ここに無い先・項目は既定(PROPOSE_ONLY)に戻る */
  entries: Array<WritebackPolicyEntryInput>;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
};

/**
 * 対象外にした理由。**黙って数から消さない**ための閉じた語彙（ADR-13）。
 * ここに載せているのは、**いま実際に立つもの**だけである。
 * 設計にある `UNREACHABLE` は、原本が取れなかったときを「対象外」と呼ぶ語だが、
 * 取れないのは失敗であって対象外ではないので載せていない（失敗は
 * `Job.failed` と `JobFailedTarget` に理由つきで残る）。
 */
export type SkipReason =
  /** すでに処理済み */
  | "ALREADY_PROCESSED"
  /** 月次の予算上限に達した（FR23・FR27.6） */
  | "BUDGET_EXHAUSTED"
  /**
   * HLS（m3u8）だけがあり、mp4 の原本が無い（ADR-13 / DEC-3）。
   *
   * **新しくは立てない。** m3u8 だけの動画も結合して処理するようにした
   * （ADR 0003）。以前の処理でこの理由が記録された動画が残っているので、
   * 値そのものは残してある。
   */
  | "HLS_ONLY"
  /**
   * mp4 の原本も HLS（m3u8）も取り込まれていない。
   *
   * 処理に渡す URL が 1 つも無い動画である。取り込み済みのデータから数えられる。
   */
  | "NO_SOURCE"
  /**
   * 原本が 200 MB を超える（ADR-13 / DEC-3）。
   *
   * **一覧の見積もりでは数えられない。** 取り込み済みのデータに大きさの列が無く、
   * 1 本ずつ原本の先頭を読みにいかないと分からないためである。
   * デコードの段で実際に読んで初めて立つ値で、走らせた結果としてだけ現れる。
   * HLS（m3u8）から処理する動画では、結合したあとの大きさで判定する。
   */
  | "TOO_LARGE";

export type SourceType = "OFFICIAL_SNS" | "OTHER" | "REVIEW" | "UGC";

export type StartFaqGenerationInput = {
  /**
   * `faqGenerationEstimate` が返した `targetCount`。
   * **必須** — 見積もりを見ずに開始できないようにするため（§2.9 と同じ形）。
   * 現在の対象件数と食い違えば `CONFLICT`（対象が増減した ＝ 見積もりが古い）。
   */
  acknowledgedTargetCount: number;
  idempotencyKey?: string | null | undefined;
  /**
   * どのルールで作るか。**設計の SDL には無い加算的なフィールド**。
   * 省略すると、生成器に組み込まれている既定の指示で作る（商品詳細からの
   * 起動は従来どおりこの形で、ルールを選ばなくても動く）。
   * 指定したルールに配信中の版が無ければ `BAD_USER_INPUT` で断る
   * （どの版で作ったか辿れない答えを残さない）。
   */
  ruleId?: string | number | null | undefined;
  target: FaqGenerationTargetInput;
};

/**
 * 商品ページを組み立てる対象。
 *
 * **商品を名指しするしかない**（母数の全件を受けない）。この縦切りでは配信が
 * 無いので、テナント全部のページを一度に作る理由が無く、作れば台帳の payload が
 * その件数ぶん膨らむだけである。上限は `startPageGeneration` の説明を参照。
 */
export type StartPageGenerationInput = {
  idempotencyKey?: string | null | undefined;
  productIds: Array<string | number>;
};

/** 登録の往復 1。**直前にパスワードを入れ直させる**(乗っ取った側が登録できないように) */
export type StartPasskeyRegistrationInput = {
  password: string;
};

/**
 * 一括処理を始める入力。
 *
 * **見積もりを見てからでないと始められない**（`acknowledgedTargetCount` が必須）。
 */
export type StartVideoMetaBatchInput = {
  /**
   * `videoMetaBatchEstimate` が返した `targetCount`（まだ処理していない動画の本数）。
   * **必須** — 見積もりを見ずに開始できないようにするため。
   * いまの対象件数と食い違えば `CONFLICT`（対象が増減した ＝ 見積もりが古い）。
   */
  acknowledgedTargetCount: number;
  idempotencyKey?: string | null | undefined;
  /**
   * この 1 回で処理する本数の上限。1〜50。
   *
   * **処理済み（`DONE`）の動画は対象にしない。** 投稿の新しい順に並べ、
   * まだ処理していない動画（読み取りに失敗した動画を含む）だけをこの本数まで
   * 詰める。処理済みを読み直すのは名指し（`enqueueVideoMetaJob` の
   * `reprocess`）だけである。新しい順に見る行数には上限（1,000 行）があり、
   * そこまでで本数に届かなければ、集まったぶんで始める（そのことは、起票した
   * ジョブの見込み `estimate.basis.note` に書く）。
   *
   * 対象の全件をそのまま流さない — 数百本を 1 回で流すと、費用も所要時間も
   * 止められなくなる。続きは、終わってからもう一度始める。
   */
  limit?: number;
};

export type TenantRole =
  "CS_DELEGATE" | "TENANT_ADMIN" | "TENANT_EDITOR" | "TENANT_VIEWER";

export type UnconfirmSegmentProductInput = {
  /** 取り消す確定の版。食い違えば `CONFLICT` */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  segmentIndex: number;
  videoId: string | number;
};

export type UpdateKnowledgeAttributeInput = {
  attribute: string;
  /**
   * **必須**。読まずに書ける経路を作らない。
   * 1 度も人が触っていないシートは 0 を渡す。
   */
  expectedVersion: number;
  group: string;
  idempotencyKey?: string | null | undefined;
  productId: string | number;
  /**
   * 単数の属性に入れる値。`value` も `values` も渡さなければ、その属性の
   * 人手の値を**取り下げる**（機械生成の値に戻る）。
   * `values: []` は「人手で未取得と確定した」で、取り下げとは別の意味。
   */
  value?: string | null | undefined;
  /** 複数値の属性に入れる値。単数の属性に渡したら BAD_USER_INPUT */
  values?: Array<string> | null | undefined;
};

export type UpdatePersonaInput = {
  attributes?: Array<string> | null | undefined;
  barriers?: Array<string> | null | undefined;
  description?: string | null | undefined;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  name?: string | null | undefined;
  personaId: string | number;
  /**
   * このペルソナが知りたいこと（観点）の全量。省略すると現状のまま。
   * 渡したときは**この配列がそのまま次の一覧になる**（差分ではない）。
   * 本文が一致する観点は id と有効・無効をそのまま引き継ぎ、並びは配列の順になる。
   *
   * 観点 1 件ずつの有効・無効の切り替えと注記の編集は、専用の mutation を足す PR で
   * 入れる（この縦切りでは、その 2 つを変える手段がそもそも無い）。
   */
  questions?: Array<string> | null | undefined;
};

export type UpdatePersonaQuestionInput = {
  enabled?: boolean | null | undefined;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  note?: string | null | undefined;
  order?: number | null | undefined;
  personaId: string | number;
  question?: string | null | undefined;
  questionId: string | number;
};

export type UpdateRegulationBookInput = {
  addBannedPhrases?: Array<string> | null | undefined;
  addTones?: Array<string> | null | undefined;
  addWordingRules?: Array<WordingRuleInput> | null | undefined;
  /**
   * **必須**。読まずに書ける経路を作らない。
   * 1 度も登録が無ければ 0 を渡す。
   */
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  removeBannedPhraseIds?: Array<string | number> | null | undefined;
  removeToneIds?: Array<string | number> | null | undefined;
  removeWordingRuleIds?: Array<string | number> | null | undefined;
};

export type UpdateRuleDraftInput = {
  answerMaxChars?: number | null | undefined;
  banned?: Array<string> | null | undefined;
  expectedVersion: number;
  idempotencyKey?: string | null | undefined;
  maxPerProduct?: number | null | undefined;
  minEvidenceCount?: number | null | undefined;
  promptBody?: string | null | undefined;
  ruleId: string | number;
  scope?: string | null | undefined;
};

export type UpsertBrandContextInput = {
  body: string;
  /** 省略すると新規。渡すとその 1 件を置き換える */
  brandContextId?: string | number | null | undefined;
  idempotencyKey?: string | null | undefined;
  label: string;
  /** null ならテナント全体に紐づく */
  personaId?: string | number | null | undefined;
};

export type ValueSource =
  "INFERRED" | "MANUAL" | "MASTER" | "NONE" | "REVIEW" | "UGC" | "VIDEO";

/** 根拠の種類。読解が実際に見たものを指す */
export type VideoEvidenceKind =
  /** 動画から切り出した静止画 */
  | "FRAME"
  /** 文字起こしの行 */
  | "TRANSCRIPT";

/** 動画メタ一覧の絞り込み */
export type VideoMetaFilter = {
  /**
   * 商品候補の結びつき。読解の結果にしかないので、`DONE` / `FAILED` と同じく
   * 引く向きが逆になる。
   *
   * **読解していない動画は 1 本も返らない。** 結びつきは読解の結果であって、
   * まだ読んでいない動画には「候補なし」も含めて何も記録が無い。
   */
  linkState?: LinkState | null | undefined;
  /**
   * 処理の状態。
   *
   * `PENDING` / `SKIPPED` は取り込み済みのデータの側で絞る。
   * `DONE` / `FAILED` は読解の結果にしかないので、**引く向きを逆にして**
   * 読解の結果から該当する動画を引き、取り込み済みのデータを ID で引き当てる。
   *
   * `RUNNING` では絞れない。実行中であることは読解の結果に記録されないので、
   * 渡すと `BAD_USER_INPUT` で断る（絞ったつもりで 0 件が返るのを避ける）。
   *
   * 逆に引いたときの該当が上限（500 本）を超えた場合も `BAD_USER_INPUT` で
   * 断る。**黙って切らない** — 切って返すと、画面は「これで全部」と読む。
   */
  processState?: VideoProcessState | null | undefined;
};

/**
 * 並び順。**画面の既定は `DURATION_DESC`**。
 *
 * 設計の既定は `PLAY_COUNT_DESC`（再生数の多い順）だったが、再生数を動画 1 本に
 * 紐づけられる取り込み元が無いので、実在する列だけで並べる。
 * `CONFIDENCE_ASC` は読解の生成物が要るので、処理が入る PR で足す。
 */
export type VideoMetaOrder =
  /** 尺の長い順 */
  | "DURATION_DESC"
  /** 投稿の新しい順 */
  | "POSTED_AT_DESC";

export type VideoMetaTargetInput = {
  /** 尺（秒）。省略すると取り込み済みの値を使う。見積もりと区間の刻みに使う */
  durationSec?: number | null | undefined;
  /**
   * 読み取り済み（`DONE`）の動画を、もう一度読み取る。
   *
   * 既定の `false` では、読み取り済みの動画は計画の段で `ALREADY_PROCESSED` として
   * 外れる（同じ動画を二重に課金しない）。`true` のときだけ外さずに読み直し、
   * 前回の読み取り結果を置き換える。人が確定した商品は置き換えの対象外で、残る。
   */
  reprocess?: boolean | null | undefined;
  /**
   * 原本の置き場所。省略すると、取り込み済みの mp4 の URL を使う。
   *
   * **指定できるのは、この環境の決まった置き場（`s3://…`）の下だけ**である。
   * それ以外を渡すと `BAD_USER_INPUT` で断る — 取り込み元に無い外の URL を
   * 読みに行かせない。置き場が設定されていない環境では、指定そのものを断る。
   */
  sourceUri?: string | null | undefined;
  /** `videoMetaDocs` が返した `id`（`<ugcId>#<seq>`） */
  videoId: string | number;
};

/** 1 本の動画の処理状態。対象外（SKIPPED）と未処理（PENDING）を混ぜない */
export type VideoProcessState =
  /** 処理済み */
  | "DONE"
  /** 処理したが失敗した */
  | "FAILED"
  /** まだ処理していない。対象ではある */
  | "PENDING"
  /** 処理中 */
  | "RUNNING"
  /** 対象外（`skipReason` に理由が入る） */
  | "SKIPPED";

export type WordingRuleInput = {
  avoid: string;
  use: string;
};

/**
 * 自動書き戻しの許可範囲(ADR-20 / DEC-9)。
 * **run / stop とは別レイヤー。**
 *
 * ⚠️ `AUTOMATIC` を選んでも、**この PR では反映は走らない**(アダプタが 1 つも
 * つながっていない)。承認を省いたことにはせず、提案は `PENDING` のまま積まれる。
 */
export type WritebackMode =
  /** 提案がそのまま反映される。**口がつながるまで PROPOSE_ONLY と同じに動く** */
  | "AUTOMATIC"
  /** 触らない */
  | "NEVER"
  /** 提案まで。人が承認してから反映する(既定) */
  | "PROPOSE_ONLY";

export type WritebackPolicyEntryInput = {
  field?: string | null | undefined;
  mode: WritebackMode;
  target: WritebackTarget;
};

export type WritebackProposalActionInput = {
  idempotencyKey?: string | null | undefined;
  proposalId: string | number;
  reason?: string | null | undefined;
};

export type WritebackProposalFilter = {
  productId?: string | number | null | undefined;
  state?: WritebackProposalState | null | undefined;
  target?: WritebackTarget | null | undefined;
};

export type WritebackProposalState =
  "APPLIED" | "APPROVED" | "FAILED" | "PENDING" | "REJECTED" | "REVERTED";

/** 書き戻し先。EC 側の API が未確定なので**差し替えられる口(アダプタ)**として定義する(FR30.2) */
export type WritebackTarget =
  /** 投稿と商品へのタグ付与 */
  | "CONTENT_TAG"
  /** EC の商品属性 */
  | "PRODUCT_ATTRIBUTE"
  /** EC の商品詳細の原稿 */
  | "PRODUCT_COPY"
  /** タグ配信の並び順 */
  | "TAG_FEED_ORDER";

export type AiConnectionsQueryVariables = Exact<{ [key: string]: never }>;

export type AiConnectionsQuery = {
  aiConnections: Array<{
    id: string;
    memberName: string;
    memberId: string;
    department: string | null;
    client: AiClientKind;
    model: string | null;
    modelSource: string;
    clientIdMasked: string;
    connectedAt: string;
    lastUsedAt: string | null;
    monthlyCalls: number | null;
    status: AiConnectionStatus;
    role: TenantRole;
    shared: Array<{
      key: string;
      label: string;
      enabled: boolean;
      locked: boolean;
      note: string | null;
    }>;
    operations: Array<{
      permission: PolicyAction;
      label: string;
      enabled: boolean;
      locked: boolean;
    }>;
  }>;
  aiConnectGuides: Array<{
    client: AiClientKind;
    endpointUrl: string;
    steps: Array<string>;
    note: string | null;
  }>;
};

export type IssueAiConnectionMutationVariables = Exact<{
  input: IssueAiConnectionInput;
}>;

export type IssueAiConnectionMutation = {
  issueAiConnection: {
    plaintext: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    connection: {
      id: string;
      memberName: string;
      clientIdMasked: string;
      status: AiConnectionStatus;
    } | null;
  };
};

export type SetAiConnectionStatusMutationVariables = Exact<{
  input: SetAiConnectionStatusInput;
}>;

export type SetAiConnectionStatusMutation = {
  setAiConnectionStatus: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    connection: { id: string; status: AiConnectionStatus } | null;
  };
};

export type AioHubQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
}>;

export type AioHubQuery = {
  contextCoverage: {
    answerRate: number | null;
    computedAt: string | null;
    basis: { label: string; note: string; denominator: number | null };
    stages: Array<{
      no: number;
      title: string;
      summary: string;
      metric: string | null;
      metricNote: string;
      state: PipelineState;
      stateLabel: string;
      links: Array<{ label: string; route: string }>;
      dataGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    }>;
    nextActions: Array<{
      id: string;
      title: string;
      detail: string;
      expectedQuestionGain: number | null;
      route: string;
    }>;
  };
  sourceHoldings: Array<{
    source: string;
    label: string;
    have: number | null;
    note: string;
    dataGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  }>;
};

export type ApiAccessQueryVariables = Exact<{ [key: string]: never }>;

export type ApiAccessQuery = {
  apiUsage: Array<{ period: string; calls: number; errors: number }>;
  apiEndpointDocs: Array<{
    method: string;
    path: string;
    returns: string;
    rateLimit: string;
  }>;
  embedSnippets: Array<{ kind: string; label: string; code: string }>;
  apiKeys: Array<{
    id: string;
    masked: string;
    label: string;
    scope: ApiKeyScope;
    createdAt: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
    version: number;
    createdBy: { id: string; displayName: string } | null;
  }>;
  allowedOrigins: {
    origins: Array<string>;
    version: number;
    updatedAt: string | null;
    editableByViewer: boolean;
  };
};

export type CreateApiKeyMutationVariables = Exact<{
  input: CreateApiKeyInput;
}>;

export type CreateApiKeyMutation = {
  createApiKey: {
    plaintext: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    apiKey: {
      id: string;
      masked: string;
      label: string;
      version: number;
    } | null;
  };
};

export type RevokeApiKeyMutationVariables = Exact<{
  input: RevokeApiKeyInput;
}>;

export type RevokeApiKeyMutation = {
  revokeApiKey: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    apiKey: { id: string; revokedAt: string | null; version: number } | null;
  };
};

export type SetAllowedOriginsMutationVariables = Exact<{
  input: SetAllowedOriginsInput;
}>;

export type SetAllowedOriginsMutation = {
  setAllowedOrigins: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    allowedOrigins: {
      origins: Array<string>;
      version: number;
      updatedAt: string | null;
      editableByViewer: boolean;
    } | null;
  };
};

export type AuditEventsQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  filter?: AuditFilter | null | undefined;
}>;

export type AuditEventsQuery = {
  auditEvents: {
    totalCount: number | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        at: string;
        kind: AuditEventKind;
        actorLabel: string;
        onBehalfOf: string | null;
        delegated: boolean;
        resource: PolicyResource;
        resourceId: string | null;
        summary: string;
        before: unknown;
        after: unknown;
        jobId: string | null;
        notified: boolean | null;
        requestId: string;
      };
    }>;
  };
};

export type ContextCoverageQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
}>;

export type ContextCoverageQuery = {
  contextCoverage: {
    answerRate: number | null;
    computedAt: string | null;
    basis: { label: string; note: string; denominator: number | null };
    stages: Array<{
      no: number;
      title: string;
      summary: string;
      metric: string | null;
      metricNote: string;
      state: PipelineState;
      stateLabel: string;
      links: Array<{ label: string; route: string }>;
      dataGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    }>;
    nextActions: Array<{
      id: string;
      title: string;
      detail: string;
      expectedQuestionGain: number | null;
      route: string;
    }>;
  };
};

export type EndDelegationMutationVariables = Exact<{ [key: string]: never }>;

export type EndDelegationMutation = {
  endDelegation: {
    ended: boolean;
    userErrors: Array<{ code: string; message: string }>;
  };
};

export type FaqDuplicatesQueryVariables = Exact<{
  productId: string | number;
}>;

export type FaqDuplicatesQuery = {
  faqDuplicates: {
    productId: string;
    faqTotal: number;
    removedCount: number;
    rekeyedCount: number;
    remainingCount: number;
    overLimit: boolean;
    groups: Array<{
      dedupKey: string;
      evidenceLabel: string;
      keep: {
        faqId: string;
        question: string;
        delivered: boolean;
        generatedAt: string;
      };
      remove: Array<{
        faqId: string;
        question: string;
        delivered: boolean;
        generatedAt: string;
      }>;
    }>;
  };
};

export type MergeDuplicateFaqsMutationVariables = Exact<{
  input: MergeDuplicateFaqsInput;
}>;

export type MergeDuplicateFaqsMutation = {
  mergeDuplicateFaqs: {
    removedCount: number;
    remainingCount: number;
    auditEventId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type JobEstimateFieldsFragment = {
  targetCount: number;
  skippedCount: number;
  estimatedCost: number | null;
  estimatedCurrency: string | null;
  estimatedSeconds: number | null;
  estimatedLlmCalls: number;
  skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
  basis: { label: string; note: string; denominator: number | null };
  costGap: {
    what: string;
    why: string;
    needs: string;
    external: string | null;
  } | null;
};

export type JobProgressFieldsFragment = {
  id: string;
  kind: JobKind;
  state: JobState;
  progress: number | null;
  total: number | null;
  succeeded: number | null;
  failed: number | null;
  skipped: number | null;
  deferred: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  enqueuedAt: string;
  errorSummary: string | null;
  retryable: boolean;
  retriedByJobId: string | null;
  retryOfJobId: string | null;
  cancelRequestedAt: string | null;
  cancelEffect: JobCancelEffect | null;
  suggestedPollIntervalSec: number | null;
  auditEventId: string | null;
  skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
  estimate: {
    targetCount: number;
    skippedCount: number;
    estimatedCost: number | null;
    estimatedCurrency: string | null;
    estimatedSeconds: number | null;
    estimatedLlmCalls: number;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    basis: { label: string; note: string; denominator: number | null };
    costGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  } | null;
};

export type FaqGenerationEstimateQueryVariables = Exact<{
  input: FaqGenerationTargetInput;
}>;

export type FaqGenerationEstimateQuery = {
  faqGenerationEstimate: {
    targetCount: number;
    skippedCount: number;
    estimatedCost: number | null;
    estimatedCurrency: string | null;
    estimatedSeconds: number | null;
    estimatedLlmCalls: number;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    basis: { label: string; note: string; denominator: number | null };
    costGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  };
};

export type StartFaqGenerationMutationVariables = Exact<{
  input: StartFaqGenerationInput;
}>;

export type StartFaqGenerationMutation = {
  startFaqGeneration: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type JobProgressQueryVariables = Exact<{
  id: string | number;
}>;

export type JobProgressQuery = {
  job: {
    id: string;
    kind: JobKind;
    state: JobState;
    progress: number | null;
    total: number | null;
    succeeded: number | null;
    failed: number | null;
    skipped: number | null;
    deferred: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    enqueuedAt: string;
    errorSummary: string | null;
    retryable: boolean;
    retriedByJobId: string | null;
    retryOfJobId: string | null;
    cancelRequestedAt: string | null;
    cancelEffect: JobCancelEffect | null;
    suggestedPollIntervalSec: number | null;
    auditEventId: string | null;
    failedTargets: {
      totalCount: number | null;
      pageInfo: { hasNextPage: boolean; endCursor: string | null };
      edges: Array<{
        cursor: string;
        node: {
          targetId: string;
          label: string;
          reason: string;
          failedAt: string;
        };
      }>;
    };
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    estimate: {
      targetCount: number;
      skippedCount: number;
      estimatedCost: number | null;
      estimatedCurrency: string | null;
      estimatedSeconds: number | null;
      estimatedLlmCalls: number;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      basis: { label: string; note: string; denominator: number | null };
      costGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    } | null;
  } | null;
};

export type RunningJobsQueryVariables = Exact<{
  state: JobState;
  productId: string | number;
  first?: number | null | undefined;
}>;

export type RunningJobsQuery = {
  jobs: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        kind: JobKind;
        state: JobState;
        progress: number | null;
        total: number | null;
        succeeded: number | null;
        failed: number | null;
        skipped: number | null;
        deferred: number | null;
        startedAt: string | null;
        finishedAt: string | null;
        enqueuedAt: string;
        errorSummary: string | null;
        retryable: boolean;
        retriedByJobId: string | null;
        retryOfJobId: string | null;
        cancelRequestedAt: string | null;
        cancelEffect: JobCancelEffect | null;
        suggestedPollIntervalSec: number | null;
        auditEventId: string | null;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        estimate: {
          targetCount: number;
          skippedCount: number;
          estimatedCost: number | null;
          estimatedCurrency: string | null;
          estimatedSeconds: number | null;
          estimatedLlmCalls: number;
          skippedReasons: Array<{
            reason: SkipReason;
            label: string;
            count: number;
          }>;
          basis: { label: string; note: string; denominator: number | null };
          costGap: {
            what: string;
            why: string;
            needs: string;
            external: string | null;
          } | null;
        } | null;
      };
    }>;
  };
};

export type FeaturedProductsQueryVariables = Exact<{ [key: string]: never }>;

export type FeaturedProductsQuery = {
  featuredProducts: Array<{
    id: string;
    name: string | null;
    brand: string | null;
    category: string | null;
    price: number | null;
    currency: string | null;
    imageUrl: string | null;
    enabled: boolean;
  }>;
};

export type IngestSourcesQueryVariables = Exact<{ [key: string]: never }>;

export type IngestSourcesQuery = {
  ingestSources: Array<{
    id: string;
    name: string;
    summary: string;
    tier: IngestTier;
    channel: IngestChannel;
    state: ConnectionState;
    records: number | null;
    recordsNote: string | null;
    note: string;
    experimental: boolean;
    fills: Array<{ screen: string; target: string; route: string }>;
    missingRequiredFields: Array<{
      field: string;
      label: string;
      count: number;
    }> | null;
    dataGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  }>;
};

export type CancelJobMutationVariables = Exact<{
  input: CancelJobInput;
}>;

export type CancelJobMutation = {
  cancelJob: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RetryJobMutationVariables = Exact<{
  input: RetryJobInput;
}>;

export type RetryJobMutation = {
  retryJob: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type KnowledgeCoverageQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
  minUgcCount?: number | null | undefined;
}>;

export type KnowledgeCoverageQuery = {
  knowledgeCoverage: {
    productCount: number | null;
    computedAt: string | null;
    basis: { label: string; note: string; denominator: number | null };
    groups: Array<{
      name: string;
      axis: string | null;
      filled: number | null;
      total: number | null;
      coverage: number | null;
      sources: Array<ValueSource>;
      dataGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    }>;
    sources: Array<{
      key: string;
      label: string;
      note: string;
      count: number | null;
      percent: number | null;
    }>;
    dataGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
    generation: {
      lastGeneratedAt: string | null;
      succeededCount: number;
      productCount: number;
      failedCount: number | null;
      failedDataGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    };
  };
};

export type KnowledgeSheetQueryVariables = Exact<{
  productId: string | number;
}>;

export type KnowledgeSheetQuery = {
  knowledgeSheet: {
    productId: string;
    productName: string | null;
    coverage: number | null;
    version: number;
    updatedAt: string | null;
    generatedBy: {
      modelId: string;
      promptVersion: string;
      generatedAt: string | null;
    } | null;
    groups: Array<{ group: string; filled: boolean; coverage: number | null }>;
    attributes: Array<{
      group: string;
      attribute: string;
      label: string;
      value: string | null;
      values: Array<string>;
      source: ValueSource;
      confidence: number | null;
      evidence: string;
      level: SchemaLevel;
      humanEditable: boolean;
      axis: string | null;
      multiValued: boolean;
    }>;
  } | null;
  vocabularyAxes: Array<{
    id: string;
    label: string;
    multiValued: boolean;
    termCount: number | null;
    terms: { edges: Array<{ node: { slug: string; term: string } }> };
  }>;
};

export type KnowledgeSchemaQueryVariables = Exact<{ [key: string]: never }>;

export type KnowledgeSchemaQuery = {
  attributeSchema: Array<{
    group: string;
    attribute: string;
    vocabAxis: string | null;
    note: string;
    levels: Array<{ category: string; level: SchemaLevel }>;
  }>;
  vocabularyAxes: Array<{
    id: string;
    label: string;
    origin: string | null;
    unit: string | null;
    multiValued: boolean;
    termCount: number | null;
    terms: {
      totalCount: number | null;
      pageInfo: { hasNextPage: boolean; endCursor: string | null };
      edges: Array<{
        cursor: string;
        node: {
          slug: string;
          term: string;
          synonyms: Array<string>;
          count: number | null;
        };
      }>;
    };
  }>;
};

export type UpdateKnowledgeAttributeMutationVariables = Exact<{
  input: UpdateKnowledgeAttributeInput;
}>;

export type UpdateKnowledgeAttributeMutation = {
  updateKnowledgeAttribute: {
    auditEventId: string | null;
    sheet: {
      productId: string;
      productName: string | null;
      coverage: number | null;
      version: number;
      updatedAt: string | null;
      generatedBy: {
        modelId: string;
        promptVersion: string;
        generatedAt: string | null;
      } | null;
      groups: Array<{
        group: string;
        filled: boolean;
        coverage: number | null;
      }>;
      attributes: Array<{
        group: string;
        attribute: string;
        label: string;
        value: string | null;
        values: Array<string>;
        source: ValueSource;
        confidence: number | null;
        evidence: string;
        level: SchemaLevel;
        humanEditable: boolean;
        axis: string | null;
        multiValued: boolean;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type KnowledgeSheetFieldsFragment = {
  productId: string;
  productName: string | null;
  coverage: number | null;
  version: number;
  updatedAt: string | null;
  generatedBy: {
    modelId: string;
    promptVersion: string;
    generatedAt: string | null;
  } | null;
  groups: Array<{ group: string; filled: boolean; coverage: number | null }>;
  attributes: Array<{
    group: string;
    attribute: string;
    label: string;
    value: string | null;
    values: Array<string>;
    source: ValueSource;
    confidence: number | null;
    evidence: string;
    level: SchemaLevel;
    humanEditable: boolean;
    axis: string | null;
    multiValued: boolean;
  }>;
};

export type VocabularyChoiceFieldsFragment = {
  id: string;
  label: string;
  multiValued: boolean;
  termCount: number | null;
  terms: { edges: Array<{ node: { slug: string; term: string } }> };
};

export type MembersQueryVariables = Exact<{ [key: string]: never }>;

export type MembersQuery = {
  members: Array<{
    id: string;
    role: TenantRole;
    joinedAt: string | null;
    version: number;
    editableByViewer: boolean;
    user: { id: string; displayName: string; email: string | null };
  }>;
  roleMatrix: Array<{
    role: TenantRole;
    label: string;
    canRead: boolean;
    canWrite: boolean;
    canRunStop: boolean;
  }>;
};

export type SetMemberRoleMutationVariables = Exact<{
  input: SetMemberRoleInput;
}>;

export type SetMemberRoleMutation = {
  setMemberRole: {
    auditEventId: string | null;
    member: {
      id: string;
      role: TenantRole;
      version: number;
      editableByViewer: boolean;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RemoveMemberMutationVariables = Exact<{
  input: RemoveMemberInput;
}>;

export type RemoveMemberMutation = {
  removeMember: {
    removedMemberId: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type InvitationsQueryVariables = Exact<{ [key: string]: never }>;

export type InvitationsQuery = {
  invitations: Array<{
    id: string;
    email: string;
    role: TenantRole;
    status: MemberStatus;
    expiresAt: string;
  }>;
  roleMatrix: Array<{ role: TenantRole; label: string }>;
};

export type InviteMemberMutationVariables = Exact<{
  input: InviteMemberInput;
}>;

export type InviteMemberMutation = {
  inviteMember: {
    auditEventId: string | null;
    invitation: {
      id: string;
      email: string;
      role: TenantRole;
      status: MemberStatus;
      expiresAt: string;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RevokeInvitationMutationVariables = Exact<{
  input: RevokeInvitationInput;
}>;

export type RevokeInvitationMutation = {
  revokeInvitation: {
    revokedInvitationId: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type NotificationsQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  unreadOnly?: boolean | null | undefined;
}>;

export type NotificationsQuery = {
  notifications: {
    unreadCount: number;
    unreadCountCapped: boolean;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        kind: NotificationKind;
        title: string;
        body: string;
        jobId: string | null;
        route: string | null;
        createdAt: string;
        readAt: string | null;
      };
    }>;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
};

export type NotificationUnreadCountQueryVariables = Exact<{
  [key: string]: never;
}>;

export type NotificationUnreadCountQuery = {
  notifications: { unreadCount: number; unreadCountCapped: boolean };
};

export type NotificationPreferenceQueryVariables = Exact<{
  [key: string]: never;
}>;

export type NotificationPreferenceQuery = {
  notificationPreference: { inApp: boolean; email: boolean };
};

export type MarkNotificationsReadMutationVariables = Exact<{
  input: MarkNotificationsReadInput;
}>;

export type MarkNotificationsReadMutation = {
  markNotificationsRead: {
    unreadCount: number;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SetNotificationPreferenceMutationVariables = Exact<{
  input: SetNotificationPreferenceInput;
}>;

export type SetNotificationPreferenceMutation = {
  setNotificationPreference: {
    preference: { inApp: boolean; email: boolean } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type StartPageGenerationMutationVariables = Exact<{
  input: StartPageGenerationInput;
}>;

export type StartPageGenerationMutation = {
  startPageGeneration: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type PageGenerationJobsQueryVariables = Exact<{
  state: JobState;
  productId: string | number;
  first?: number | null | undefined;
}>;

export type PageGenerationJobsQuery = {
  jobs: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        kind: JobKind;
        state: JobState;
        progress: number | null;
        total: number | null;
        succeeded: number | null;
        failed: number | null;
        skipped: number | null;
        deferred: number | null;
        startedAt: string | null;
        finishedAt: string | null;
        enqueuedAt: string;
        errorSummary: string | null;
        retryable: boolean;
        retriedByJobId: string | null;
        retryOfJobId: string | null;
        cancelRequestedAt: string | null;
        cancelEffect: JobCancelEffect | null;
        suggestedPollIntervalSec: number | null;
        auditEventId: string | null;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        estimate: {
          targetCount: number;
          skippedCount: number;
          estimatedCost: number | null;
          estimatedCurrency: string | null;
          estimatedSeconds: number | null;
          estimatedLlmCalls: number;
          skippedReasons: Array<{
            reason: SkipReason;
            label: string;
            count: number;
          }>;
          basis: { label: string; note: string; denominator: number | null };
          costGap: {
            what: string;
            why: string;
            needs: string;
            external: string | null;
          } | null;
        } | null;
      };
    }>;
  };
};

export type ViewerPasskeysQueryVariables = Exact<{ [key: string]: never }>;

export type ViewerPasskeysQuery = {
  viewerPasskeys: Array<{
    id: string;
    label: string;
    deviceType: PasskeyDeviceType;
    backedUp: boolean;
    createdAt: string;
    lastUsedAt: string | null;
  }>;
};

export type StartPasskeyRegistrationMutationVariables = Exact<{
  input: StartPasskeyRegistrationInput;
}>;

export type StartPasskeyRegistrationMutation = {
  startPasskeyRegistration: {
    ticket: string | null;
    optionsJson: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type FinishPasskeyRegistrationMutationVariables = Exact<{
  input: FinishPasskeyRegistrationInput;
}>;

export type FinishPasskeyRegistrationMutation = {
  finishPasskeyRegistration: {
    passkey: {
      id: string;
      label: string;
      deviceType: PasskeyDeviceType;
      backedUp: boolean;
      createdAt: string;
      lastUsedAt: string | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RenamePasskeyMutationVariables = Exact<{
  input: RenamePasskeyInput;
}>;

export type RenamePasskeyMutation = {
  renamePasskey: {
    passkey: {
      id: string;
      label: string;
      deviceType: PasskeyDeviceType;
      backedUp: boolean;
      createdAt: string;
      lastUsedAt: string | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RemovePasskeyMutationVariables = Exact<{
  input: RemovePasskeyInput;
}>;

export type RemovePasskeyMutation = {
  removePasskey: {
    removedId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type PersonaCoveragesQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
}>;

export type PersonaCoveragesQuery = {
  personaCoverages: Array<{
    overall: number | null;
    computedAt: string | null;
    judge: string | null;
    basis: { label: string; note: string; denominator: number | null };
    dataGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
    persona: {
      id: string;
      name: string;
      description: string;
      origin: PersonaOrigin;
      brandContexts: Array<{ id: string; label: string; body: string }>;
    };
    byQuestion: Array<{
      coverage: number | null;
      covered: number | null;
      total: number | null;
      undecided: number | null;
      question: {
        id: string;
        question: string;
        enabled: boolean;
        note: string | null;
      };
      dataGap: { what: string; why: string; needs: string } | null;
    }>;
  }>;
};

export type PopulationsQueryVariables = Exact<{ [key: string]: never }>;

export type PopulationsQuery = {
  populations: Array<{
    id: PopulationId;
    label: string;
    note: string;
    products: number | null;
    answerRate: number | null;
  }>;
};

export type RecomputePersonaCoverageMutationVariables = Exact<{
  input: RecomputePersonaCoverageInput;
}>;

export type RecomputePersonaCoverageMutation = {
  recomputePersonaCoverage: {
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type PersonaCoverageFieldsFragment = {
  overall: number | null;
  computedAt: string | null;
  judge: string | null;
  basis: { label: string; note: string; denominator: number | null };
  dataGap: {
    what: string;
    why: string;
    needs: string;
    external: string | null;
  } | null;
  persona: {
    id: string;
    name: string;
    description: string;
    origin: PersonaOrigin;
    brandContexts: Array<{ id: string; label: string; body: string }>;
  };
  byQuestion: Array<{
    coverage: number | null;
    covered: number | null;
    total: number | null;
    undecided: number | null;
    question: {
      id: string;
      question: string;
      enabled: boolean;
      note: string | null;
    };
    dataGap: { what: string; why: string; needs: string } | null;
  }>;
};

export type PersonasQueryVariables = Exact<{
  includeDerived?: boolean | null | undefined;
}>;

export type PersonasQuery = {
  personas: Array<{
    id: string;
    name: string;
    description: string;
    attributes: Array<string>;
    barriers: Array<string>;
    origin: PersonaOrigin;
    version: number;
    updatedAt: string;
    updatedBy: { id: string; displayName: string } | null;
    questions: Array<{
      id: string;
      question: string;
      enabled: boolean;
      order: number;
      fromTemplateId: string | null;
      note: string | null;
    }>;
    brandContexts: Array<{
      id: string;
      personaId: string | null;
      label: string;
      body: string;
      updatedAt: string;
    }>;
  }>;
};

export type PersonaQueryVariables = Exact<{
  id: string | number;
}>;

export type PersonaQuery = {
  persona: {
    id: string;
    name: string;
    description: string;
    attributes: Array<string>;
    barriers: Array<string>;
    origin: PersonaOrigin;
    version: number;
    updatedAt: string;
    updatedBy: { id: string; displayName: string } | null;
    questions: Array<{
      id: string;
      question: string;
      enabled: boolean;
      order: number;
      fromTemplateId: string | null;
      note: string | null;
    }>;
    brandContexts: Array<{
      id: string;
      personaId: string | null;
      label: string;
      body: string;
      updatedAt: string;
    }>;
  } | null;
};

export type CreatePersonaMutationVariables = Exact<{
  input: CreatePersonaInput;
}>;

export type CreatePersonaMutation = {
  createPersona: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type UpdatePersonaMutationVariables = Exact<{
  input: UpdatePersonaInput;
}>;

export type UpdatePersonaMutation = {
  updatePersona: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type AddPersonaQuestionMutationVariables = Exact<{
  input: AddPersonaQuestionInput;
}>;

export type AddPersonaQuestionMutation = {
  addPersonaQuestion: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type UpdatePersonaQuestionMutationVariables = Exact<{
  input: UpdatePersonaQuestionInput;
}>;

export type UpdatePersonaQuestionMutation = {
  updatePersonaQuestion: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RemovePersonaQuestionMutationVariables = Exact<{
  input: RemovePersonaQuestionInput;
}>;

export type RemovePersonaQuestionMutation = {
  removePersonaQuestion: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type DeletePersonaMutationVariables = Exact<{
  input: DeletePersonaInput;
}>;

export type DeletePersonaMutation = {
  deletePersona: {
    deletedPersonaId: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type ClonePersonaFromDerivedMutationVariables = Exact<{
  input: ClonePersonaInput;
}>;

export type ClonePersonaFromDerivedMutation = {
  clonePersonaFromDerived: {
    auditEventId: string | null;
    persona: {
      id: string;
      name: string;
      description: string;
      attributes: Array<string>;
      barriers: Array<string>;
      origin: PersonaOrigin;
      version: number;
      updatedAt: string;
      updatedBy: { id: string; displayName: string } | null;
      questions: Array<{
        id: string;
        question: string;
        enabled: boolean;
        order: number;
        fromTemplateId: string | null;
        note: string | null;
      }>;
      brandContexts: Array<{
        id: string;
        personaId: string | null;
        label: string;
        body: string;
        updatedAt: string;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type UpsertBrandContextMutationVariables = Exact<{
  input: UpsertBrandContextInput;
}>;

export type UpsertBrandContextMutation = {
  upsertBrandContext: {
    auditEventId: string | null;
    brandContext: {
      id: string;
      personaId: string | null;
      label: string;
      body: string;
      updatedAt: string;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type DeleteBrandContextMutationVariables = Exact<{
  input: DeleteBrandContextInput;
}>;

export type DeleteBrandContextMutation = {
  deleteBrandContext: {
    deletedBrandContextId: string | null;
    auditEventId: string | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type BrandContextFieldsFragment = {
  id: string;
  personaId: string | null;
  label: string;
  body: string;
  updatedAt: string;
};

export type PersonaFieldsFragment = {
  id: string;
  name: string;
  description: string;
  attributes: Array<string>;
  barriers: Array<string>;
  origin: PersonaOrigin;
  version: number;
  updatedAt: string;
  updatedBy: { id: string; displayName: string } | null;
  questions: Array<{
    id: string;
    question: string;
    enabled: boolean;
    order: number;
    fromTemplateId: string | null;
    note: string | null;
  }>;
  brandContexts: Array<{
    id: string;
    personaId: string | null;
    label: string;
    body: string;
    updatedAt: string;
  }>;
};

export type PostFieldsFragment = {
  id: string;
  authorHash: string;
  sourceType: SourceType;
  title: string | null;
  body: string | null;
  bodyWithheld: boolean;
  mediaKind: MediaKind;
  mediaUrl: string | null;
  reactions: number | null;
  postedAt: string | null;
  isDisplayed: boolean;
  tagCount: number;
  productLinkCount: number;
  videoCount: number;
};

export type PostsQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  sourceType?: SourceType | null | undefined;
  query?: string | null | undefined;
  hasMedia?: boolean | null | undefined;
}>;

export type PostsQuery = {
  posts: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        authorHash: string;
        sourceType: SourceType;
        title: string | null;
        body: string | null;
        bodyWithheld: boolean;
        mediaKind: MediaKind;
        mediaUrl: string | null;
        reactions: number | null;
        postedAt: string | null;
        isDisplayed: boolean;
        tagCount: number;
        productLinkCount: number;
        videoCount: number;
      };
    }>;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    dataGap: { what: string; why: string; needs: string } | null;
  };
};

export type PostQueryVariables = Exact<{
  id: string | number;
}>;

export type PostQuery = {
  post: {
    id: string;
    authorHash: string;
    sourceType: SourceType;
    title: string | null;
    body: string | null;
    bodyWithheld: boolean;
    mediaKind: MediaKind;
    mediaUrl: string | null;
    reactions: number | null;
    postedAt: string | null;
    isDisplayed: boolean;
    tagCount: number;
    productLinkCount: number;
    videoCount: number;
  } | null;
};

export type ProductsQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  filter?: ProductFilter | null | undefined;
  order?: ProductOrder | null | undefined;
  population?: PopulationId | null | undefined;
  minUgcCount?: number | null | undefined;
}>;

export type ProductsQuery = {
  products: {
    totalCount: number | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        name: string | null;
        brand: string | null;
        category: string | null;
        sku: string | null;
        price: number | null;
        currency: string | null;
        imageUrl: string | null;
        enabled: boolean;
        aio: {
          coverage: number | null;
          faqTotal: number;
          faqDelivered: number;
          axesFilled: number;
          axesTotal: number;
          publish: PublishState;
        };
        answerability: {
          covered: number;
          decided: number;
          faqCovered: number;
        } | null;
      };
    }>;
  };
};

export type ProductQueryVariables = Exact<{
  id: string | number;
}>;

export type ProductQuery = {
  product: {
    id: string;
    name: string | null;
    brand: string | null;
    category: string | null;
    audience: string | null;
    sku: string | null;
    price: number | null;
    currency: string | null;
    productUrl: string | null;
    imageUrl: string | null;
    enabled: boolean;
    description: string | null;
    descriptionSources: Array<string>;
    dataCounts: {
      reviews: number | null;
      staffPosts: number | null;
      socialPosts: number | null;
      videos: number | null;
      localAssets: number | null;
    };
    idealContext: {
      audience: string;
      concerns: string;
      scenes: string;
      value: string;
    } | null;
    aio: {
      coverage: number | null;
      faqTotal: number;
      faqDelivered: number;
      axesFilled: number;
      axesTotal: number;
      publish: PublishState;
      tags: Array<{
        axis: string;
        label: string;
        values: Array<string>;
        source: ValueSource;
      }>;
      faqs: Array<{
        id: string;
        ruleId: string;
        question: string;
        answer: string;
        confidence: number | null;
        delivered: boolean;
        generatedAt: string;
        generator: string | null;
        evidence: { type: EvidenceType; label: string; count: number | null };
      }>;
    };
    answerability: {
      covered: number;
      decided: number;
      faqCovered: number;
      computedAt: string;
      byPersona: Array<{
        personaId: string;
        personaName: string | null;
        covered: number;
        decided: number;
        faqCovered: number;
      }>;
    } | null;
    page: {
      productId: string;
      body: string;
      jsonld: unknown;
      generatedAt: string;
      promptVersions: Array<string>;
      faqCount: number;
      builder: string;
    } | null;
    dataGaps: Array<{
      what: string;
      why: string;
      needs: string;
      external: string | null;
    }>;
  } | null;
};

export type PublishOverviewQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
}>;

export type PublishOverviewQuery = {
  publishTargets: Array<{
    kind: PublishTargetKind;
    label: string;
    detail: string;
    enabled: boolean;
    available: boolean;
    unavailableReason: string | null;
    lastPublishedAt: string | null;
  }>;
  publishScope: {
    condition: string;
    matched: number | null;
    generated: number | null;
    excluded: number | null;
    excludeReason: string;
    stoppedRules: Array<string>;
    basis: { label: string; note: string; denominator: number | null };
  };
  deliveryLog: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        at: string;
        target: PublishTargetKind;
        products: number;
        result: string;
      };
    }>;
    pageInfo: { hasNextPage: boolean };
  };
};

export type WritebackPolicyQueryVariables = Exact<{ [key: string]: never }>;

export type WritebackPolicyQuery = {
  writebackPolicy: {
    version: number;
    defaultMode: WritebackMode;
    entries: Array<{
      target: WritebackTarget;
      field: string | null;
      mode: WritebackMode;
    }>;
    adapters: Array<{
      target: WritebackTarget;
      name: string;
      connected: boolean;
      note: string | null;
    }>;
  };
};

export type WritebackProposalsQueryVariables = Exact<{
  filter?: WritebackProposalFilter | null | undefined;
}>;

export type WritebackProposalsQuery = {
  writebackProposals: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        target: WritebackTarget;
        field: string | null;
        productId: string | null;
        state: WritebackProposalState;
        proposedValue: string;
        previousValue: string | null;
        reason: string;
        mode: WritebackMode;
        approvalSkipped: boolean;
        proposedAt: string;
        approvedAt: string | null;
        appliedAt: string | null;
        revertedAt: string | null;
        approvedBy: { id: string; displayName: string } | null;
      };
    }>;
    pageInfo: { hasNextPage: boolean };
  };
};

export type WritebackProposalFieldsFragment = {
  id: string;
  target: WritebackTarget;
  field: string | null;
  productId: string | null;
  state: WritebackProposalState;
  proposedValue: string;
  previousValue: string | null;
  reason: string;
  mode: WritebackMode;
  approvalSkipped: boolean;
  proposedAt: string;
  approvedAt: string | null;
  appliedAt: string | null;
  revertedAt: string | null;
  approvedBy: { id: string; displayName: string } | null;
};

export type SetPublishTargetMutationVariables = Exact<{
  input: SetPublishTargetInput;
}>;

export type SetPublishTargetMutation = {
  setPublishTarget: {
    auditEventId: string | null;
    publishTarget: {
      kind: PublishTargetKind;
      enabled: boolean;
      available: boolean;
      lastPublishedAt: string | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SetWritebackPolicyMutationVariables = Exact<{
  input: SetWritebackPolicyInput;
}>;

export type SetWritebackPolicyMutation = {
  setWritebackPolicy: {
    auditEventId: string | null;
    writebackPolicy: {
      version: number;
      defaultMode: WritebackMode;
      entries: Array<{
        target: WritebackTarget;
        field: string | null;
        mode: WritebackMode;
      }>;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type ProposeProductPageWritebackMutationVariables = Exact<{
  input: ProposeProductPageWritebackInput;
}>;

export type ProposeProductPageWritebackMutation = {
  proposeProductPageWriteback: {
    auditEventId: string | null;
    proposal: {
      id: string;
      target: WritebackTarget;
      field: string | null;
      productId: string | null;
      state: WritebackProposalState;
      proposedValue: string;
      previousValue: string | null;
      reason: string;
      mode: WritebackMode;
      approvalSkipped: boolean;
      proposedAt: string;
      approvedAt: string | null;
      appliedAt: string | null;
      revertedAt: string | null;
      approvedBy: { id: string; displayName: string } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type ApproveWritebackProposalMutationVariables = Exact<{
  input: WritebackProposalActionInput;
}>;

export type ApproveWritebackProposalMutation = {
  approveWritebackProposal: {
    auditEventId: string | null;
    proposal: {
      id: string;
      target: WritebackTarget;
      field: string | null;
      productId: string | null;
      state: WritebackProposalState;
      proposedValue: string;
      previousValue: string | null;
      reason: string;
      mode: WritebackMode;
      approvalSkipped: boolean;
      proposedAt: string;
      approvedAt: string | null;
      appliedAt: string | null;
      revertedAt: string | null;
      approvedBy: { id: string; displayName: string } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RejectWritebackProposalMutationVariables = Exact<{
  input: WritebackProposalActionInput;
}>;

export type RejectWritebackProposalMutation = {
  rejectWritebackProposal: {
    auditEventId: string | null;
    proposal: {
      id: string;
      target: WritebackTarget;
      field: string | null;
      productId: string | null;
      state: WritebackProposalState;
      proposedValue: string;
      previousValue: string | null;
      reason: string;
      mode: WritebackMode;
      approvalSkipped: boolean;
      proposedAt: string;
      approvedAt: string | null;
      appliedAt: string | null;
      revertedAt: string | null;
      approvedBy: { id: string; displayName: string } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type QuestionMapQueryVariables = Exact<{
  population?: PopulationId | null | undefined;
}>;

export type QuestionMapQuery = {
  questionTemplates: Array<{
    id: string;
    question: string;
    origin: QuestionOrigin;
    ruleId: string | null;
    ruleState: RuleState | null;
    scope: string | null;
    enabled: boolean;
    answerCount: number | null;
    note: string | null;
  }>;
  questionCoverage: Array<{
    question: string;
    coverage: number | null;
    available: Array<string>;
    missing: Array<string>;
    basis: { label: string; note: string; denominator: number | null };
  }>;
  rules: Array<{ id: string; name: string; state: RuleState }>;
};

export type AddQuestionTemplateMutationVariables = Exact<{
  input: AddQuestionTemplateInput;
}>;

export type AddQuestionTemplateMutation = {
  addQuestionTemplate: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    template: {
      id: string;
      question: string;
      scope: string | null;
      ruleId: string | null;
      enabled: boolean;
    } | null;
  };
};

export type SetQuestionTemplateEnabledMutationVariables = Exact<{
  input: SetQuestionTemplateEnabledInput;
}>;

export type SetQuestionTemplateEnabledMutation = {
  setQuestionTemplateEnabled: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    template: { id: string; enabled: boolean } | null;
  };
};

export type RuleSummaryFieldsFragment = {
  id: string;
  name: string;
  description: string;
  state: RuleState;
  scope: string | null;
  publishedPromptVersion: string | null;
  maxPerProduct: number;
  minEvidenceCount: number | null;
  answerMaxChars: number | null;
  questions: Array<string>;
  banned: Array<string>;
  ruleVersion: number;
  version: number;
  lastRunAt: string | null;
};

export type RuleDetailFieldsFragment = {
  id: string;
  name: string;
  description: string;
  state: RuleState;
  scope: string | null;
  publishedPromptVersion: string | null;
  maxPerProduct: number;
  minEvidenceCount: number | null;
  answerMaxChars: number | null;
  questions: Array<string>;
  banned: Array<string>;
  ruleVersion: number;
  version: number;
  lastRunAt: string | null;
  draft: {
    promptVersion: string;
    promptBody: string;
    maxPerProduct: number;
    minEvidenceCount: number | null;
    answerMaxChars: number | null;
    banned: Array<string>;
    scope: string | null;
    updatedAt: string;
    updatedBy: { id: string; displayName: string };
  } | null;
  promptVersions: {
    totalCount: number | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: Array<{
      cursor: string;
      node: {
        promptVersion: string;
        promptBody: string;
        savedAt: string;
        isPublished: boolean;
        savedBy: { id: string; displayName: string };
      };
    }>;
  };
};

export type RulesQueryVariables = Exact<{ [key: string]: never }>;

export type RulesQuery = {
  rules: Array<{
    id: string;
    name: string;
    description: string;
    state: RuleState;
    scope: string | null;
    publishedPromptVersion: string | null;
    maxPerProduct: number;
    minEvidenceCount: number | null;
    answerMaxChars: number | null;
    questions: Array<string>;
    banned: Array<string>;
    ruleVersion: number;
    version: number;
    lastRunAt: string | null;
  }>;
};

export type RuleQueryVariables = Exact<{
  id: string | number;
}>;

export type RuleQuery = {
  rule: {
    id: string;
    name: string;
    description: string;
    state: RuleState;
    scope: string | null;
    publishedPromptVersion: string | null;
    maxPerProduct: number;
    minEvidenceCount: number | null;
    answerMaxChars: number | null;
    questions: Array<string>;
    banned: Array<string>;
    ruleVersion: number;
    version: number;
    lastRunAt: string | null;
    draft: {
      promptVersion: string;
      promptBody: string;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      banned: Array<string>;
      scope: string | null;
      updatedAt: string;
      updatedBy: { id: string; displayName: string };
    } | null;
    promptVersions: {
      totalCount: number | null;
      pageInfo: { hasNextPage: boolean; endCursor: string | null };
      edges: Array<{
        cursor: string;
        node: {
          promptVersion: string;
          promptBody: string;
          savedAt: string;
          isPublished: boolean;
          savedBy: { id: string; displayName: string };
        };
      }>;
    };
  } | null;
};

export type CreateRuleMutationVariables = Exact<{
  input: CreateRuleInput;
}>;

export type CreateRuleMutation = {
  createRule: {
    auditEventId: string | null;
    rule: {
      id: string;
      name: string;
      description: string;
      state: RuleState;
      scope: string | null;
      publishedPromptVersion: string | null;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      questions: Array<string>;
      banned: Array<string>;
      ruleVersion: number;
      version: number;
      lastRunAt: string | null;
      draft: {
        promptVersion: string;
        promptBody: string;
        maxPerProduct: number;
        minEvidenceCount: number | null;
        answerMaxChars: number | null;
        banned: Array<string>;
        scope: string | null;
        updatedAt: string;
        updatedBy: { id: string; displayName: string };
      } | null;
      promptVersions: {
        totalCount: number | null;
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        edges: Array<{
          cursor: string;
          node: {
            promptVersion: string;
            promptBody: string;
            savedAt: string;
            isPublished: boolean;
            savedBy: { id: string; displayName: string };
          };
        }>;
      };
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type UpdateRuleDraftMutationVariables = Exact<{
  input: UpdateRuleDraftInput;
}>;

export type UpdateRuleDraftMutation = {
  updateRuleDraft: {
    auditEventId: string | null;
    rule: {
      id: string;
      name: string;
      description: string;
      state: RuleState;
      scope: string | null;
      publishedPromptVersion: string | null;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      questions: Array<string>;
      banned: Array<string>;
      ruleVersion: number;
      version: number;
      lastRunAt: string | null;
      draft: {
        promptVersion: string;
        promptBody: string;
        maxPerProduct: number;
        minEvidenceCount: number | null;
        answerMaxChars: number | null;
        banned: Array<string>;
        scope: string | null;
        updatedAt: string;
        updatedBy: { id: string; displayName: string };
      } | null;
      promptVersions: {
        totalCount: number | null;
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        edges: Array<{
          cursor: string;
          node: {
            promptVersion: string;
            promptBody: string;
            savedAt: string;
            isPublished: boolean;
            savedBy: { id: string; displayName: string };
          };
        }>;
      };
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SaveRuleVersionMutationVariables = Exact<{
  input: SaveRuleVersionInput;
}>;

export type SaveRuleVersionMutation = {
  saveRuleVersion: {
    auditEventId: string | null;
    rule: {
      id: string;
      name: string;
      description: string;
      state: RuleState;
      scope: string | null;
      publishedPromptVersion: string | null;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      questions: Array<string>;
      banned: Array<string>;
      ruleVersion: number;
      version: number;
      lastRunAt: string | null;
      draft: {
        promptVersion: string;
        promptBody: string;
        maxPerProduct: number;
        minEvidenceCount: number | null;
        answerMaxChars: number | null;
        banned: Array<string>;
        scope: string | null;
        updatedAt: string;
        updatedBy: { id: string; displayName: string };
      } | null;
      promptVersions: {
        totalCount: number | null;
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        edges: Array<{
          cursor: string;
          node: {
            promptVersion: string;
            promptBody: string;
            savedAt: string;
            isPublished: boolean;
            savedBy: { id: string; displayName: string };
          };
        }>;
      };
    } | null;
    promptVersion: {
      promptVersion: string;
      savedAt: string;
      isPublished: boolean;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SetPublishedPromptVersionMutationVariables = Exact<{
  input: SetPublishedPromptVersionInput;
}>;

export type SetPublishedPromptVersionMutation = {
  setPublishedPromptVersion: {
    auditEventId: string | null;
    rule: {
      id: string;
      name: string;
      description: string;
      state: RuleState;
      scope: string | null;
      publishedPromptVersion: string | null;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      questions: Array<string>;
      banned: Array<string>;
      ruleVersion: number;
      version: number;
      lastRunAt: string | null;
      draft: {
        promptVersion: string;
        promptBody: string;
        maxPerProduct: number;
        minEvidenceCount: number | null;
        answerMaxChars: number | null;
        banned: Array<string>;
        scope: string | null;
        updatedAt: string;
        updatedBy: { id: string; displayName: string };
      } | null;
      promptVersions: {
        totalCount: number | null;
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        edges: Array<{
          cursor: string;
          node: {
            promptVersion: string;
            promptBody: string;
            savedAt: string;
            isPublished: boolean;
            savedBy: { id: string; displayName: string };
          };
        }>;
      };
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SetRuleStateMutationVariables = Exact<{
  input: SetRuleStateInput;
}>;

export type SetRuleStateMutation = {
  setRuleState: {
    auditEventId: string | null;
    rule: {
      id: string;
      name: string;
      description: string;
      state: RuleState;
      scope: string | null;
      publishedPromptVersion: string | null;
      maxPerProduct: number;
      minEvidenceCount: number | null;
      answerMaxChars: number | null;
      questions: Array<string>;
      banned: Array<string>;
      ruleVersion: number;
      version: number;
      lastRunAt: string | null;
      draft: {
        promptVersion: string;
        promptBody: string;
        maxPerProduct: number;
        minEvidenceCount: number | null;
        answerMaxChars: number | null;
        banned: Array<string>;
        scope: string | null;
        updatedAt: string;
        updatedBy: { id: string; displayName: string };
      } | null;
      promptVersions: {
        totalCount: number | null;
        pageInfo: { hasNextPage: boolean; endCursor: string | null };
        edges: Array<{
          cursor: string;
          node: {
            promptVersion: string;
            promptBody: string;
            savedAt: string;
            isPublished: boolean;
            savedBy: { id: string; displayName: string };
          };
        }>;
      };
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type DryRunJobFieldsFragment = {
  droppedFaqs: number;
  id: string;
  kind: JobKind;
  state: JobState;
  progress: number | null;
  total: number | null;
  succeeded: number | null;
  failed: number | null;
  skipped: number | null;
  deferred: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  enqueuedAt: string;
  errorSummary: string | null;
  retryable: boolean;
  retriedByJobId: string | null;
  retryOfJobId: string | null;
  cancelRequestedAt: string | null;
  cancelEffect: JobCancelEffect | null;
  suggestedPollIntervalSec: number | null;
  auditEventId: string | null;
  droppedFaqReasons: Array<{
    reason: FaqDropReason;
    label: string;
    count: number;
  }>;
  skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
  estimate: {
    targetCount: number;
    skippedCount: number;
    estimatedCost: number | null;
    estimatedCurrency: string | null;
    estimatedSeconds: number | null;
    estimatedLlmCalls: number;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    basis: { label: string; note: string; denominator: number | null };
    costGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  } | null;
};

export type DryRunFieldsFragment = {
  job: {
    droppedFaqs: number;
    id: string;
    kind: JobKind;
    state: JobState;
    progress: number | null;
    total: number | null;
    succeeded: number | null;
    failed: number | null;
    skipped: number | null;
    deferred: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    enqueuedAt: string;
    errorSummary: string | null;
    retryable: boolean;
    retriedByJobId: string | null;
    retryOfJobId: string | null;
    cancelRequestedAt: string | null;
    cancelEffect: JobCancelEffect | null;
    suggestedPollIntervalSec: number | null;
    auditEventId: string | null;
    droppedFaqReasons: Array<{
      reason: FaqDropReason;
      label: string;
      count: number;
    }>;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    estimate: {
      targetCount: number;
      skippedCount: number;
      estimatedCost: number | null;
      estimatedCurrency: string | null;
      estimatedSeconds: number | null;
      estimatedLlmCalls: number;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      basis: { label: string; note: string; denominator: number | null };
      costGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    } | null;
  };
  comparison: {
    generatedBefore: number;
    generatedAfter: number;
    bannedHitsBefore: number;
    bannedHitsAfter: number;
    answers: Array<{
      productId: string;
      question: string;
      before: string | null;
      beforeNote: string | null;
      after: string | null;
      afterNote: string | null;
      changed: boolean;
    }>;
  } | null;
};

export type DryRunRuleMutationVariables = Exact<{
  input: DryRunRuleInput;
}>;

export type DryRunRuleMutation = {
  dryRunRule: {
    auditEventId: string | null;
    result: {
      job: {
        droppedFaqs: number;
        id: string;
        kind: JobKind;
        state: JobState;
        progress: number | null;
        total: number | null;
        succeeded: number | null;
        failed: number | null;
        skipped: number | null;
        deferred: number | null;
        startedAt: string | null;
        finishedAt: string | null;
        enqueuedAt: string;
        errorSummary: string | null;
        retryable: boolean;
        retriedByJobId: string | null;
        retryOfJobId: string | null;
        cancelRequestedAt: string | null;
        cancelEffect: JobCancelEffect | null;
        suggestedPollIntervalSec: number | null;
        auditEventId: string | null;
        droppedFaqReasons: Array<{
          reason: FaqDropReason;
          label: string;
          count: number;
        }>;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        estimate: {
          targetCount: number;
          skippedCount: number;
          estimatedCost: number | null;
          estimatedCurrency: string | null;
          estimatedSeconds: number | null;
          estimatedLlmCalls: number;
          skippedReasons: Array<{
            reason: SkipReason;
            label: string;
            count: number;
          }>;
          basis: { label: string; note: string; denominator: number | null };
          costGap: {
            what: string;
            why: string;
            needs: string;
            external: string | null;
          } | null;
        } | null;
      };
      comparison: {
        generatedBefore: number;
        generatedAfter: number;
        bannedHitsBefore: number;
        bannedHitsAfter: number;
        answers: Array<{
          productId: string;
          question: string;
          before: string | null;
          beforeNote: string | null;
          after: string | null;
          afterNote: string | null;
          changed: boolean;
        }>;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type DryRunQueryVariables = Exact<{
  jobId: string | number;
}>;

export type DryRunQuery = {
  dryRun: {
    job: {
      droppedFaqs: number;
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      droppedFaqReasons: Array<{
        reason: FaqDropReason;
        label: string;
        count: number;
      }>;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    };
    comparison: {
      generatedBefore: number;
      generatedAfter: number;
      bannedHitsBefore: number;
      bannedHitsAfter: number;
      answers: Array<{
        productId: string;
        question: string;
        before: string | null;
        beforeNote: string | null;
        after: string | null;
        afterNote: string | null;
        changed: boolean;
      }>;
    } | null;
  } | null;
};

export type RuleJobProgressQueryVariables = Exact<{
  id: string | number;
}>;

export type RuleJobProgressQuery = {
  job: {
    droppedFaqs: number;
    id: string;
    kind: JobKind;
    state: JobState;
    progress: number | null;
    total: number | null;
    succeeded: number | null;
    failed: number | null;
    skipped: number | null;
    deferred: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    enqueuedAt: string;
    errorSummary: string | null;
    retryable: boolean;
    retriedByJobId: string | null;
    retryOfJobId: string | null;
    cancelRequestedAt: string | null;
    cancelEffect: JobCancelEffect | null;
    suggestedPollIntervalSec: number | null;
    auditEventId: string | null;
    droppedFaqReasons: Array<{
      reason: FaqDropReason;
      label: string;
      count: number;
    }>;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    estimate: {
      targetCount: number;
      skippedCount: number;
      estimatedCost: number | null;
      estimatedCurrency: string | null;
      estimatedSeconds: number | null;
      estimatedLlmCalls: number;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      basis: { label: string; note: string; denominator: number | null };
      costGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    } | null;
  } | null;
};

export type TenantRunningJobsQueryVariables = Exact<{
  kind: JobKind;
  state: JobState;
  first?: number | null | undefined;
}>;

export type TenantRunningJobsQuery = {
  jobs: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        droppedFaqs: number;
        id: string;
        kind: JobKind;
        state: JobState;
        progress: number | null;
        total: number | null;
        succeeded: number | null;
        failed: number | null;
        skipped: number | null;
        deferred: number | null;
        startedAt: string | null;
        finishedAt: string | null;
        enqueuedAt: string;
        errorSummary: string | null;
        retryable: boolean;
        retriedByJobId: string | null;
        retryOfJobId: string | null;
        cancelRequestedAt: string | null;
        cancelEffect: JobCancelEffect | null;
        suggestedPollIntervalSec: number | null;
        auditEventId: string | null;
        droppedFaqReasons: Array<{
          reason: FaqDropReason;
          label: string;
          count: number;
        }>;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        estimate: {
          targetCount: number;
          skippedCount: number;
          estimatedCost: number | null;
          estimatedCurrency: string | null;
          estimatedSeconds: number | null;
          estimatedLlmCalls: number;
          skippedReasons: Array<{
            reason: SkipReason;
            label: string;
            count: number;
          }>;
          basis: { label: string; note: string; denominator: number | null };
          costGap: {
            what: string;
            why: string;
            needs: string;
            external: string | null;
          } | null;
        } | null;
      };
    }>;
  };
};

export type RunStopPolicyQueryVariables = Exact<{ [key: string]: never }>;

export type RunStopPolicyQuery = {
  runStopPolicy: {
    value: RunStopPolicyValue;
    version: number;
    updatedAt: string | null;
    editableByViewer: boolean;
    updatedBy: { id: string; displayName: string } | null;
  };
};

export type SetRunStopPolicyMutationVariables = Exact<{
  input: SetRunStopPolicyInput;
}>;

export type SetRunStopPolicyMutation = {
  setRunStopPolicy: {
    auditEventId: string | null;
    runStopPolicy: {
      value: RunStopPolicyValue;
      version: number;
      updatedAt: string | null;
      editableByViewer: boolean;
      updatedBy: { id: string; displayName: string } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type SellingAnglesQueryVariables = Exact<{
  productId: string | number;
}>;

export type SellingAnglesQuery = {
  sellingAngles: Array<{
    key: string;
    productId: string;
    cut: string;
    attributes: Array<string>;
    attributeSource: ValueSource;
    idea: string | null;
    adoption: Adoption;
    dataGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  }>;
};

export type SellingAngleFieldsFragment = {
  key: string;
  productId: string;
  cut: string;
  attributes: Array<string>;
  attributeSource: ValueSource;
  idea: string | null;
  adoption: Adoption;
  dataGap: {
    what: string;
    why: string;
    needs: string;
    external: string | null;
  } | null;
};

export type SellingProductChoicesQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  filter?: ProductFilter | null | undefined;
}>;

export type SellingProductChoicesQuery = {
  products: {
    totalCount: number | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: Array<{ node: { id: string; name: string | null } }>;
  };
};

export type SetSellingAngleAdoptionMutationVariables = Exact<{
  input: SetSellingAngleAdoptionInput;
}>;

export type SetSellingAngleAdoptionMutation = {
  setSellingAngleAdoption: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    angle: {
      key: string;
      productId: string;
      cut: string;
      attributes: Array<string>;
      attributeSource: ValueSource;
      idea: string | null;
      adoption: Adoption;
      dataGap: {
        what: string;
        why: string;
        needs: string;
        external: string | null;
      } | null;
    } | null;
  };
};

export type RegulationBookQueryVariables = Exact<{ [key: string]: never }>;

export type RegulationBookQuery = {
  regulationBook: {
    version: number;
    updatedAt: string | null;
    tones: Array<{
      id: string;
      text: string;
      addedAt: string;
      addedBy: { id: string; displayName: string };
    }>;
    bannedPhrases: Array<{
      id: string;
      text: string;
      addedAt: string;
      addedBy: { id: string; displayName: string };
    }>;
    wordingRules: Array<{
      id: string;
      use: string;
      avoid: string;
      addedAt: string;
      addedBy: { id: string; displayName: string };
    }>;
  };
};

export type RegulationBookFieldsFragment = {
  version: number;
  updatedAt: string | null;
  tones: Array<{
    id: string;
    text: string;
    addedAt: string;
    addedBy: { id: string; displayName: string };
  }>;
  bannedPhrases: Array<{
    id: string;
    text: string;
    addedAt: string;
    addedBy: { id: string; displayName: string };
  }>;
  wordingRules: Array<{
    id: string;
    use: string;
    avoid: string;
    addedAt: string;
    addedBy: { id: string; displayName: string };
  }>;
};

export type UpdateRegulationBookMutationVariables = Exact<{
  input: UpdateRegulationBookInput;
}>;

export type UpdateRegulationBookMutation = {
  updateRegulationBook: {
    auditEventId: string | null;
    userErrors: Array<{
      code: string;
      message: string;
      field: Array<string> | null;
    }>;
    regulationBook: {
      version: number;
      updatedAt: string | null;
      tones: Array<{
        id: string;
        text: string;
        addedAt: string;
        addedBy: { id: string; displayName: string };
      }>;
      bannedPhrases: Array<{
        id: string;
        text: string;
        addedAt: string;
        addedBy: { id: string; displayName: string };
      }>;
      wordingRules: Array<{
        id: string;
        use: string;
        avoid: string;
        addedAt: string;
        addedBy: { id: string; displayName: string };
      }>;
    } | null;
  };
};

export type VideoMetaAutoIngestQueryVariables = Exact<{ [key: string]: never }>;

export type VideoMetaAutoIngestQuery = {
  videoMetaAutoIngest: {
    enabled: boolean;
    monthlyLimit: number | null;
    processedThisMonth: number;
    suspendedByLimit: boolean;
    lastDetectedAt: string | null;
    version: number;
    updatedAt: string | null;
    editableByViewer: boolean;
    updatedBy: { id: string; displayName: string } | null;
  };
};

export type SetVideoMetaAutoIngestMutationVariables = Exact<{
  input: SetVideoMetaAutoIngestInput;
}>;

export type SetVideoMetaAutoIngestMutation = {
  setVideoMetaAutoIngest: {
    auditEventId: string | null;
    autoIngest: {
      enabled: boolean;
      monthlyLimit: number | null;
      processedThisMonth: number;
      suspendedByLimit: boolean;
      lastDetectedAt: string | null;
      version: number;
      updatedAt: string | null;
      editableByViewer: boolean;
      updatedBy: { id: string; displayName: string } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type VideoMetaDocsQueryVariables = Exact<{
  first?: number | null | undefined;
  after?: string | null | undefined;
  filter?: VideoMetaFilter | null | undefined;
  order?: VideoMetaOrder | null | undefined;
}>;

export type VideoMetaDocsQuery = {
  videoMetaDocs: {
    totalCount: number | null;
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        ugcId: string;
        seq: number;
        videoUrl: string | null;
        hlsUrl: string | null;
        thumbnailUrl: string | null;
        durationSec: number | null;
        width: number | null;
        height: number | null;
        postedAt: string | null;
        processState: VideoProcessState;
        skipReason: SkipReason | null;
        processNote: string | null;
        linkState: LinkState;
        audioState: AudioState;
        processGap: { what: string; why: string; needs: string } | null;
      };
    }>;
  };
};

export type VideoMetaBatchEstimateQueryVariables = Exact<{
  [key: string]: never;
}>;

export type VideoMetaBatchEstimateQuery = {
  videoMetaBatchEstimate: {
    processedCount: number | null;
    processedCountExact: boolean | null;
    targetCount: number;
    skippedCount: number;
    estimatedCost: number | null;
    estimatedCurrency: string | null;
    estimatedSeconds: number | null;
    estimatedLlmCalls: number;
    skippedReasons: Array<{ reason: SkipReason; label: string; count: number }>;
    basis: { label: string; note: string; denominator: number | null };
    costGap: {
      what: string;
      why: string;
      needs: string;
      external: string | null;
    } | null;
  };
};

export type StartVideoMetaBatchMutationVariables = Exact<{
  input: StartVideoMetaBatchInput;
}>;

export type StartVideoMetaBatchMutation = {
  startVideoMetaBatch: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type RunningVideoMetaJobsQueryVariables = Exact<{
  state: JobState;
  first?: number | null | undefined;
}>;

export type RunningVideoMetaJobsQuery = {
  jobs: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: {
        id: string;
        kind: JobKind;
        state: JobState;
        progress: number | null;
        total: number | null;
        succeeded: number | null;
        failed: number | null;
        skipped: number | null;
        deferred: number | null;
        startedAt: string | null;
        finishedAt: string | null;
        enqueuedAt: string;
        errorSummary: string | null;
        retryable: boolean;
        retriedByJobId: string | null;
        retryOfJobId: string | null;
        cancelRequestedAt: string | null;
        cancelEffect: JobCancelEffect | null;
        suggestedPollIntervalSec: number | null;
        auditEventId: string | null;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        estimate: {
          targetCount: number;
          skippedCount: number;
          estimatedCost: number | null;
          estimatedCurrency: string | null;
          estimatedSeconds: number | null;
          estimatedLlmCalls: number;
          skippedReasons: Array<{
            reason: SkipReason;
            label: string;
            count: number;
          }>;
          basis: { label: string; note: string; denominator: number | null };
          costGap: {
            what: string;
            why: string;
            needs: string;
            external: string | null;
          } | null;
        } | null;
      };
    }>;
  };
};

export type VideoMetaDocQueryVariables = Exact<{
  id: string | number;
}>;

export type VideoMetaDocQuery = {
  videoMetaDoc: {
    id: string;
    ugcId: string;
    seq: number;
    videoUrl: string | null;
    hlsUrl: string | null;
    thumbnailUrl: string | null;
    durationSec: number | null;
    width: number | null;
    height: number | null;
    postedAt: string | null;
    processState: VideoProcessState;
    skipReason: SkipReason | null;
    processNote: string | null;
    sourceType: SourceType | null;
    hasAudio: boolean | null;
    summary: string | null;
    mainProducts: Array<string> | null;
    mainTopics: Array<string> | null;
    peopleSummary: string | null;
    audioSummary: string | null;
    linkState: LinkState;
    minConfidence: number | null;
    audioState: AudioState;
    hasOnScreenText: boolean;
    interpreterId: string | null;
    interpretedAt: string | null;
    frames: Array<{ index: number; atSec: number; url: string }> | null;
    processGap: { what: string; why: string; needs: string } | null;
    segments: Array<{
      index: number;
      startSec: number;
      endSec: number;
      label: string;
      topics: Array<string> | null;
      onScreenText: Array<string> | null;
      people: Array<{
        roleGuess: string;
        count: number;
        description: string;
        confidence: number;
      }> | null;
      evidence: Array<{ kind: VideoEvidenceKind; index: number }>;
      products: Array<{
        label: string;
        basis: CandidateBasis;
        confidence: number;
        vocabTags: Array<string> | null;
      }>;
      confirmation: {
        productId: string;
        productName: string;
        confirmedAt: string;
        confirmedByUserId: string;
        version: number;
      } | null;
    }>;
    transcript: Array<{
      index: number;
      startSec: number;
      endSec: number;
      text: string;
    }>;
  } | null;
};

export type VideoSegmentFieldsFragment = {
  index: number;
  startSec: number;
  endSec: number;
  label: string;
  topics: Array<string> | null;
  onScreenText: Array<string> | null;
  people: Array<{
    roleGuess: string;
    count: number;
    description: string;
    confidence: number;
  }> | null;
  evidence: Array<{ kind: VideoEvidenceKind; index: number }>;
  products: Array<{
    label: string;
    basis: CandidateBasis;
    confidence: number;
    vocabTags: Array<string> | null;
  }>;
  confirmation: {
    productId: string;
    productName: string;
    confirmedAt: string;
    confirmedByUserId: string;
    version: number;
  } | null;
};

export type ConfirmSegmentProductMutationVariables = Exact<{
  input: ConfirmSegmentProductInput;
}>;

export type ConfirmSegmentProductMutation = {
  confirmSegmentProduct: {
    auditEventId: string | null;
    segment: {
      index: number;
      startSec: number;
      endSec: number;
      label: string;
      topics: Array<string> | null;
      onScreenText: Array<string> | null;
      people: Array<{
        roleGuess: string;
        count: number;
        description: string;
        confidence: number;
      }> | null;
      evidence: Array<{ kind: VideoEvidenceKind; index: number }>;
      products: Array<{
        label: string;
        basis: CandidateBasis;
        confidence: number;
        vocabTags: Array<string> | null;
      }>;
      confirmation: {
        productId: string;
        productName: string;
        confirmedAt: string;
        confirmedByUserId: string;
        version: number;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type UnconfirmSegmentProductMutationVariables = Exact<{
  input: UnconfirmSegmentProductInput;
}>;

export type UnconfirmSegmentProductMutation = {
  unconfirmSegmentProduct: {
    auditEventId: string | null;
    segment: {
      index: number;
      startSec: number;
      endSec: number;
      label: string;
      topics: Array<string> | null;
      onScreenText: Array<string> | null;
      people: Array<{
        roleGuess: string;
        count: number;
        description: string;
        confidence: number;
      }> | null;
      evidence: Array<{ kind: VideoEvidenceKind; index: number }>;
      products: Array<{
        label: string;
        basis: CandidateBasis;
        confidence: number;
        vocabTags: Array<string> | null;
      }>;
      confirmation: {
        productId: string;
        productName: string;
        confirmedAt: string;
        confirmedByUserId: string;
        version: number;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type VideoSegmentProductChoicesQueryVariables = Exact<{
  query?: string | null | undefined;
  first?: number | null | undefined;
}>;

export type VideoSegmentProductChoicesQuery = {
  products: {
    totalCount: number | null;
    edges: Array<{
      cursor: string;
      node: { id: string; name: string | null; brand: string | null };
    }>;
  };
};

export type EnqueueVideoMetaJobMutationVariables = Exact<{
  input: EnqueueVideoMetaJobInput;
}>;

export type EnqueueVideoMetaJobMutation = {
  enqueueVideoMetaJob: {
    auditEventId: string | null;
    job: {
      id: string;
      kind: JobKind;
      state: JobState;
      progress: number | null;
      total: number | null;
      succeeded: number | null;
      failed: number | null;
      skipped: number | null;
      deferred: number | null;
      startedAt: string | null;
      finishedAt: string | null;
      enqueuedAt: string;
      errorSummary: string | null;
      retryable: boolean;
      retriedByJobId: string | null;
      retryOfJobId: string | null;
      cancelRequestedAt: string | null;
      cancelEffect: JobCancelEffect | null;
      suggestedPollIntervalSec: number | null;
      auditEventId: string | null;
      skippedReasons: Array<{
        reason: SkipReason;
        label: string;
        count: number;
      }>;
      estimate: {
        targetCount: number;
        skippedCount: number;
        estimatedCost: number | null;
        estimatedCurrency: string | null;
        estimatedSeconds: number | null;
        estimatedLlmCalls: number;
        skippedReasons: Array<{
          reason: SkipReason;
          label: string;
          count: number;
        }>;
        basis: { label: string; note: string; denominator: number | null };
        costGap: {
          what: string;
          why: string;
          needs: string;
          external: string | null;
        } | null;
      } | null;
    } | null;
    userErrors: Array<{
      field: Array<string> | null;
      code: string;
      message: string;
    }>;
  };
};

export type ViewerQueryVariables = Exact<{ [key: string]: never }>;

export type ViewerQuery = {
  viewer: {
    role: TenantRole;
    permissions: Array<PolicyAction>;
    isOperator: boolean;
    user: { id: string; displayName: string };
    tenant: { id: string; label: string };
    memberships: Array<{
      role: TenantRole;
      permissions: Array<PolicyAction>;
      tenant: { id: string; label: string; createdAt: string };
    }>;
    delegation: {
      id: string;
      reason: DelegationReason;
      reasonNote: string | null;
      startedAt: string;
      expiresAt: string;
      actorLabel: string;
    } | null;
  };
};

export const KnowledgeSheetFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "KnowledgeSheetFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "KnowledgeSheet" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "productName" } },
          { kind: "Field", name: { kind: "Name", value: "coverage" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "generatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "modelId" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "generatedAt" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "groups" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "filled" } },
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "attributes" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "attribute" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "value" } },
                { kind: "Field", name: { kind: "Name", value: "values" } },
                { kind: "Field", name: { kind: "Name", value: "source" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "evidence" } },
                { kind: "Field", name: { kind: "Name", value: "level" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "humanEditable" },
                },
                { kind: "Field", name: { kind: "Name", value: "axis" } },
                { kind: "Field", name: { kind: "Name", value: "multiValued" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<KnowledgeSheetFieldsFragment, unknown>;
export const VocabularyChoiceFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VocabularyChoiceFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VocabularyAxis" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "multiValued" } },
          { kind: "Field", name: { kind: "Name", value: "termCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "terms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "200" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "slug" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<VocabularyChoiceFieldsFragment, unknown>;
export const PersonaCoverageFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaCoverageFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "PersonaCoverage" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "overall" } },
          { kind: "Field", name: { kind: "Name", value: "computedAt" } },
          { kind: "Field", name: { kind: "Name", value: "judge" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "dataGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "persona" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "origin" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "brandContexts" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "body" } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "byQuestion" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
                { kind: "Field", name: { kind: "Name", value: "covered" } },
                { kind: "Field", name: { kind: "Name", value: "total" } },
                { kind: "Field", name: { kind: "Name", value: "undecided" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "question" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PersonaCoverageFieldsFragment, unknown>;
export const BrandContextFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<BrandContextFieldsFragment, unknown>;
export const PersonaFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PersonaFieldsFragment, unknown>;
export const PostFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PostFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Post" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "authorHash" } },
          { kind: "Field", name: { kind: "Name", value: "sourceType" } },
          { kind: "Field", name: { kind: "Name", value: "title" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "bodyWithheld" } },
          { kind: "Field", name: { kind: "Name", value: "mediaKind" } },
          { kind: "Field", name: { kind: "Name", value: "mediaUrl" } },
          { kind: "Field", name: { kind: "Name", value: "reactions" } },
          { kind: "Field", name: { kind: "Name", value: "postedAt" } },
          { kind: "Field", name: { kind: "Name", value: "isDisplayed" } },
          { kind: "Field", name: { kind: "Name", value: "tagCount" } },
          { kind: "Field", name: { kind: "Name", value: "productLinkCount" } },
          { kind: "Field", name: { kind: "Name", value: "videoCount" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PostFieldsFragment, unknown>;
export const WritebackProposalFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "WritebackProposalFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "WritebackProposal" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "target" } },
          { kind: "Field", name: { kind: "Name", value: "field" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "proposedValue" } },
          { kind: "Field", name: { kind: "Name", value: "previousValue" } },
          { kind: "Field", name: { kind: "Name", value: "reason" } },
          { kind: "Field", name: { kind: "Name", value: "mode" } },
          { kind: "Field", name: { kind: "Name", value: "approvalSkipped" } },
          { kind: "Field", name: { kind: "Name", value: "proposedAt" } },
          { kind: "Field", name: { kind: "Name", value: "approvedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "approvedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "appliedAt" } },
          { kind: "Field", name: { kind: "Name", value: "revertedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<WritebackProposalFieldsFragment, unknown>;
export const RuleSummaryFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RuleSummaryFieldsFragment, unknown>;
export const RuleDetailFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RuleDetailFieldsFragment, unknown>;
export const JobEstimateFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<JobEstimateFieldsFragment, unknown>;
export const JobProgressFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<JobProgressFieldsFragment, unknown>;
export const DryRunJobFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DryRunJobFieldsFragment, unknown>;
export const DryRunFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "DryRunResult" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "job" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "DryRunJobFields" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "comparison" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answers" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "productId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "before" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "beforeNote" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "after" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "afterNote" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "changed" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DryRunFieldsFragment, unknown>;
export const SellingAngleFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "SellingAngleFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "SellingAngle" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "key" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "cut" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "attributeSource" } },
          { kind: "Field", name: { kind: "Name", value: "idea" } },
          { kind: "Field", name: { kind: "Name", value: "adoption" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "dataGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SellingAngleFieldsFragment, unknown>;
export const RegulationBookFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RegulationBookFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "RegulationBook" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "tones" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "bannedPhrases" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "wordingRules" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "use" } },
                { kind: "Field", name: { kind: "Name", value: "avoid" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RegulationBookFieldsFragment, unknown>;
export const VideoSegmentFieldsFragmentDoc = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VideoSegmentFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VideoSegment" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "index" } },
          { kind: "Field", name: { kind: "Name", value: "startSec" } },
          { kind: "Field", name: { kind: "Name", value: "endSec" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "people" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "roleGuess" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "topics" } },
          { kind: "Field", name: { kind: "Name", value: "onScreenText" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "evidence" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "index" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "basis" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "vocabTags" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "confirmation" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "productId" } },
                { kind: "Field", name: { kind: "Name", value: "productName" } },
                { kind: "Field", name: { kind: "Name", value: "confirmedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "confirmedByUserId" },
                },
                { kind: "Field", name: { kind: "Name", value: "version" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<VideoSegmentFieldsFragment, unknown>;
export const AiConnectionsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "AiConnections" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aiConnections" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "memberName" } },
                { kind: "Field", name: { kind: "Name", value: "memberId" } },
                { kind: "Field", name: { kind: "Name", value: "department" } },
                { kind: "Field", name: { kind: "Name", value: "client" } },
                { kind: "Field", name: { kind: "Name", value: "model" } },
                { kind: "Field", name: { kind: "Name", value: "modelSource" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "clientIdMasked" },
                },
                { kind: "Field", name: { kind: "Name", value: "connectedAt" } },
                { kind: "Field", name: { kind: "Name", value: "lastUsedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "monthlyCalls" },
                },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "role" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shared" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "key" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "locked" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "operations" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "permission" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "locked" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "aiConnectGuides" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "client" } },
                { kind: "Field", name: { kind: "Name", value: "endpointUrl" } },
                { kind: "Field", name: { kind: "Name", value: "steps" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AiConnectionsQuery, AiConnectionsQueryVariables>;
export const IssueAiConnectionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "IssueAiConnection" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "IssueAiConnectionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "issueAiConnection" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "plaintext" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "connection" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "memberName" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "clientIdMasked" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "status" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  IssueAiConnectionMutation,
  IssueAiConnectionMutationVariables
>;
export const SetAiConnectionStatusDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetAiConnectionStatus" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetAiConnectionStatusInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setAiConnectionStatus" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "connection" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "status" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetAiConnectionStatusMutation,
  SetAiConnectionStatusMutationVariables
>;
export const AioHubDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "AioHub" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "DELIVERED" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "contextCoverage" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "answerRate" } },
                { kind: "Field", name: { kind: "Name", value: "computedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "basis" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "denominator" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "stages" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "no" } },
                      { kind: "Field", name: { kind: "Name", value: "title" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "summary" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "metric" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "metricNote" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "state" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "stateLabel" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "links" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "route" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "dataGap" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "what" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "why" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "needs" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "external" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nextActions" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "title" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "detail" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "expectedQuestionGain" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "route" } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "sourceHoldings" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "source" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "have" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "external" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AioHubQuery, AioHubQueryVariables>;
export const ApiAccessDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "ApiAccess" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "apiUsage" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "period" } },
                { kind: "Field", name: { kind: "Name", value: "calls" } },
                { kind: "Field", name: { kind: "Name", value: "errors" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "apiEndpointDocs" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "method" } },
                { kind: "Field", name: { kind: "Name", value: "path" } },
                { kind: "Field", name: { kind: "Name", value: "returns" } },
                { kind: "Field", name: { kind: "Name", value: "rateLimit" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "embedSnippets" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "code" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "apiKeys" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "masked" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "createdAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "createdBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "lastUsedAt" } },
                { kind: "Field", name: { kind: "Name", value: "revokedAt" } },
                { kind: "Field", name: { kind: "Name", value: "version" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "allowedOrigins" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "origins" } },
                { kind: "Field", name: { kind: "Name", value: "version" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "editableByViewer" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ApiAccessQuery, ApiAccessQueryVariables>;
export const CreateApiKeyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CreateApiKey" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "CreateApiKeyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "createApiKey" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "plaintext" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "apiKey" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "masked" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CreateApiKeyMutation,
  CreateApiKeyMutationVariables
>;
export const RevokeApiKeyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RevokeApiKey" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RevokeApiKeyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "revokeApiKey" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "apiKey" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "revokedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RevokeApiKeyMutation,
  RevokeApiKeyMutationVariables
>;
export const SetAllowedOriginsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetAllowedOrigins" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetAllowedOriginsInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setAllowedOrigins" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "allowedOrigins" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "origins" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "updatedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "editableByViewer" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetAllowedOriginsMutation,
  SetAllowedOriginsMutationVariables
>;
export const AuditEventsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "AuditEvents" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "filter" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "AuditFilter" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "auditEvents" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "filter" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "at" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "kind" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "actorLabel" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "onBehalfOf" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "delegated" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "resource" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "resourceId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "summary" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "before" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "after" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "jobId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "notified" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "requestId" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AuditEventsQuery, AuditEventsQueryVariables>;
export const ContextCoverageDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "ContextCoverage" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "DELIVERED" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "contextCoverage" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "answerRate" } },
                { kind: "Field", name: { kind: "Name", value: "computedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "basis" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "denominator" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "stages" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "no" } },
                      { kind: "Field", name: { kind: "Name", value: "title" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "summary" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "metric" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "metricNote" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "state" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "stateLabel" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "links" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "route" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "dataGap" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "what" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "why" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "needs" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "external" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nextActions" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "title" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "detail" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "expectedQuestionGain" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "route" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ContextCoverageQuery,
  ContextCoverageQueryVariables
>;
export const EndDelegationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "EndDelegation" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "endDelegation" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "ended" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  EndDelegationMutation,
  EndDelegationMutationVariables
>;
export const FaqDuplicatesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "FaqDuplicates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "productId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "faqDuplicates" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "productId" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "productId" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "productId" } },
                { kind: "Field", name: { kind: "Name", value: "faqTotal" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "removedCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rekeyedCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "remainingCount" },
                },
                { kind: "Field", name: { kind: "Name", value: "overLimit" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "groups" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "dedupKey" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "evidenceLabel" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "keep" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "faqId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "question" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "delivered" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "generatedAt" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "remove" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "faqId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "question" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "delivered" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "generatedAt" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FaqDuplicatesQuery, FaqDuplicatesQueryVariables>;
export const MergeDuplicateFaqsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "MergeDuplicateFaqs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "MergeDuplicateFaqsInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "mergeDuplicateFaqs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "removedCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "remainingCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MergeDuplicateFaqsMutation,
  MergeDuplicateFaqsMutationVariables
>;
export const FaqGenerationEstimateDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "FaqGenerationEstimate" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "FaqGenerationTargetInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "faqGenerationEstimate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FaqGenerationEstimateQuery,
  FaqGenerationEstimateQueryVariables
>;
export const StartFaqGenerationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "StartFaqGeneration" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartFaqGenerationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "startFaqGeneration" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartFaqGenerationMutation,
  StartFaqGenerationMutationVariables
>;
export const JobProgressDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "JobProgress" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "job" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobProgressFields" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "failedTargets" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "first" },
                      value: { kind: "IntValue", value: "20" },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "totalCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "pageInfo" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "hasNextPage" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "endCursor" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "edges" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "cursor" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "node" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "targetId" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "reason" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "failedAt" },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<JobProgressQuery, JobProgressQueryVariables>;
export const RunningJobsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "RunningJobs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "state" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "JobState" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "productId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "5" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "jobs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "kind" },
                      value: { kind: "EnumValue", value: "FAQ_GENERATION" },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "state" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "state" },
                      },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "productId" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "productId" },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "JobProgressFields",
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RunningJobsQuery, RunningJobsQueryVariables>;
export const FeaturedProductsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "FeaturedProducts" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "featuredProducts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "brand" } },
                { kind: "Field", name: { kind: "Name", value: "category" } },
                { kind: "Field", name: { kind: "Name", value: "price" } },
                { kind: "Field", name: { kind: "Name", value: "currency" } },
                { kind: "Field", name: { kind: "Name", value: "imageUrl" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FeaturedProductsQuery,
  FeaturedProductsQueryVariables
>;
export const IngestSourcesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "IngestSources" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "ingestSources" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "summary" } },
                { kind: "Field", name: { kind: "Name", value: "tier" } },
                { kind: "Field", name: { kind: "Name", value: "channel" } },
                { kind: "Field", name: { kind: "Name", value: "state" } },
                { kind: "Field", name: { kind: "Name", value: "records" } },
                { kind: "Field", name: { kind: "Name", value: "recordsNote" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "experimental" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fills" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "screen" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "target" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "route" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "missingRequiredFields" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "external" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<IngestSourcesQuery, IngestSourcesQueryVariables>;
export const CancelJobDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CancelJob" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "CancelJobInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "cancelJob" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CancelJobMutation, CancelJobMutationVariables>;
export const RetryJobDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RetryJob" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RetryJobInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "retryJob" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RetryJobMutation, RetryJobMutationVariables>;
export const KnowledgeCoverageDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "KnowledgeCoverage" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "ALL" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "minUgcCount" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "knowledgeCoverage" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "minUgcCount" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "minUgcCount" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "productCount" },
                },
                { kind: "Field", name: { kind: "Name", value: "computedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "basis" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "denominator" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "groups" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "axis" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "filled" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "total" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "coverage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sources" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "dataGap" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "what" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "why" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "needs" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "external" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sources" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "key" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "percent" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "external" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generation" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "lastGeneratedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "succeededCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "productCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "failedCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "failedDataGap" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "what" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "why" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "needs" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "external" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  KnowledgeCoverageQuery,
  KnowledgeCoverageQueryVariables
>;
export const KnowledgeSheetDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "KnowledgeSheet" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "productId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "knowledgeSheet" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "productId" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "productId" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "KnowledgeSheetFields" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vocabularyAxes" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "VocabularyChoiceFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "KnowledgeSheetFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "KnowledgeSheet" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "productName" } },
          { kind: "Field", name: { kind: "Name", value: "coverage" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "generatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "modelId" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "generatedAt" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "groups" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "filled" } },
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "attributes" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "attribute" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "value" } },
                { kind: "Field", name: { kind: "Name", value: "values" } },
                { kind: "Field", name: { kind: "Name", value: "source" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "evidence" } },
                { kind: "Field", name: { kind: "Name", value: "level" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "humanEditable" },
                },
                { kind: "Field", name: { kind: "Name", value: "axis" } },
                { kind: "Field", name: { kind: "Name", value: "multiValued" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VocabularyChoiceFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VocabularyAxis" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "multiValued" } },
          { kind: "Field", name: { kind: "Name", value: "termCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "terms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "200" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "slug" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<KnowledgeSheetQuery, KnowledgeSheetQueryVariables>;
export const KnowledgeSchemaDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "KnowledgeSchema" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "attributeSchema" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "attribute" } },
                { kind: "Field", name: { kind: "Name", value: "vocabAxis" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "levels" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "category" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "level" } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vocabularyAxes" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "origin" } },
                { kind: "Field", name: { kind: "Name", value: "unit" } },
                { kind: "Field", name: { kind: "Name", value: "multiValued" } },
                { kind: "Field", name: { kind: "Name", value: "termCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "terms" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "first" },
                      value: { kind: "IntValue", value: "200" },
                    },
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "totalCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "pageInfo" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "hasNextPage" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "endCursor" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "edges" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "cursor" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "node" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "slug" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "synonyms" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  KnowledgeSchemaQuery,
  KnowledgeSchemaQueryVariables
>;
export const UpdateKnowledgeAttributeDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateKnowledgeAttribute" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpdateKnowledgeAttributeInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateKnowledgeAttribute" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sheet" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "KnowledgeSheetFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "KnowledgeSheetFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "KnowledgeSheet" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "productName" } },
          { kind: "Field", name: { kind: "Name", value: "coverage" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "generatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "modelId" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "generatedAt" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "groups" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "filled" } },
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "attributes" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "group" } },
                { kind: "Field", name: { kind: "Name", value: "attribute" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "value" } },
                { kind: "Field", name: { kind: "Name", value: "values" } },
                { kind: "Field", name: { kind: "Name", value: "source" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "evidence" } },
                { kind: "Field", name: { kind: "Name", value: "level" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "humanEditable" },
                },
                { kind: "Field", name: { kind: "Name", value: "axis" } },
                { kind: "Field", name: { kind: "Name", value: "multiValued" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdateKnowledgeAttributeMutation,
  UpdateKnowledgeAttributeMutationVariables
>;
export const MembersDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Members" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "members" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "user" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "email" } },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "role" } },
                { kind: "Field", name: { kind: "Name", value: "joinedAt" } },
                { kind: "Field", name: { kind: "Name", value: "version" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "editableByViewer" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "roleMatrix" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "role" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "canRead" } },
                { kind: "Field", name: { kind: "Name", value: "canWrite" } },
                { kind: "Field", name: { kind: "Name", value: "canRunStop" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MembersQuery, MembersQueryVariables>;
export const SetMemberRoleDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetMemberRole" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetMemberRoleInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setMemberRole" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "member" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "role" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "editableByViewer" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetMemberRoleMutation,
  SetMemberRoleMutationVariables
>;
export const RemoveMemberDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RemoveMember" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RemoveMemberInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "removeMember" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "removedMemberId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RemoveMemberMutation,
  RemoveMemberMutationVariables
>;
export const InvitationsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Invitations" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "invitations" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "email" } },
                { kind: "Field", name: { kind: "Name", value: "role" } },
                { kind: "Field", name: { kind: "Name", value: "status" } },
                { kind: "Field", name: { kind: "Name", value: "expiresAt" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "roleMatrix" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "role" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<InvitationsQuery, InvitationsQueryVariables>;
export const InviteMemberDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "InviteMember" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "InviteMemberInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "inviteMember" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "invitation" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "email" } },
                      { kind: "Field", name: { kind: "Name", value: "role" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "status" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "expiresAt" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  InviteMemberMutation,
  InviteMemberMutationVariables
>;
export const RevokeInvitationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RevokeInvitation" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RevokeInvitationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "revokeInvitation" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "revokedInvitationId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RevokeInvitationMutation,
  RevokeInvitationMutationVariables
>;
export const NotificationsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Notifications" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "20" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "unreadOnly" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Boolean" } },
          defaultValue: { kind: "BooleanValue", value: false },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "notifications" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "unreadOnly" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "unreadOnly" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "kind" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "title" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "body" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "jobId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "route" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "createdAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "readAt" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "unreadCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "unreadCountCapped" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NotificationsQuery, NotificationsQueryVariables>;
export const NotificationUnreadCountDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "NotificationUnreadCount" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "notifications" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "1" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "unreadCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "unreadCountCapped" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  NotificationUnreadCountQuery,
  NotificationUnreadCountQueryVariables
>;
export const NotificationPreferenceDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "NotificationPreference" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "notificationPreference" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "inApp" } },
                { kind: "Field", name: { kind: "Name", value: "email" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  NotificationPreferenceQuery,
  NotificationPreferenceQueryVariables
>;
export const MarkNotificationsReadDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "MarkNotificationsRead" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "MarkNotificationsReadInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "markNotificationsRead" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "unreadCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  MarkNotificationsReadMutation,
  MarkNotificationsReadMutationVariables
>;
export const SetNotificationPreferenceDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetNotificationPreference" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetNotificationPreferenceInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setNotificationPreference" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "preference" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "inApp" } },
                      { kind: "Field", name: { kind: "Name", value: "email" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetNotificationPreferenceMutation,
  SetNotificationPreferenceMutationVariables
>;
export const StartPageGenerationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "StartPageGeneration" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartPageGenerationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "startPageGeneration" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartPageGenerationMutation,
  StartPageGenerationMutationVariables
>;
export const PageGenerationJobsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "PageGenerationJobs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "state" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "JobState" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "productId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "5" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "jobs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "kind" },
                      value: { kind: "EnumValue", value: "PAGE_GENERATION" },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "state" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "state" },
                      },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "productId" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "productId" },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "JobProgressFields",
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  PageGenerationJobsQuery,
  PageGenerationJobsQueryVariables
>;
export const ViewerPasskeysDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "ViewerPasskeys" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "viewerPasskeys" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "deviceType" } },
                { kind: "Field", name: { kind: "Name", value: "backedUp" } },
                { kind: "Field", name: { kind: "Name", value: "createdAt" } },
                { kind: "Field", name: { kind: "Name", value: "lastUsedAt" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ViewerPasskeysQuery, ViewerPasskeysQueryVariables>;
export const StartPasskeyRegistrationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "StartPasskeyRegistration" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartPasskeyRegistrationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "startPasskeyRegistration" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "ticket" } },
                { kind: "Field", name: { kind: "Name", value: "optionsJson" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartPasskeyRegistrationMutation,
  StartPasskeyRegistrationMutationVariables
>;
export const FinishPasskeyRegistrationDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "FinishPasskeyRegistration" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "FinishPasskeyRegistrationInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "finishPasskeyRegistration" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "passkey" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "deviceType" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "backedUp" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "createdAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "lastUsedAt" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FinishPasskeyRegistrationMutation,
  FinishPasskeyRegistrationMutationVariables
>;
export const RenamePasskeyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RenamePasskey" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RenamePasskeyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "renamePasskey" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "passkey" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "deviceType" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "backedUp" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "createdAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "lastUsedAt" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RenamePasskeyMutation,
  RenamePasskeyMutationVariables
>;
export const RemovePasskeyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RemovePasskey" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RemovePasskeyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "removePasskey" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "removedId" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RemovePasskeyMutation,
  RemovePasskeyMutationVariables
>;
export const PersonaCoveragesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "PersonaCoverages" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "DELIVERED" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "personaCoverages" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PersonaCoverageFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaCoverageFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "PersonaCoverage" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "overall" } },
          { kind: "Field", name: { kind: "Name", value: "computedAt" } },
          { kind: "Field", name: { kind: "Name", value: "judge" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "dataGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "persona" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "origin" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "brandContexts" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "body" } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "byQuestion" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
                { kind: "Field", name: { kind: "Name", value: "covered" } },
                { kind: "Field", name: { kind: "Name", value: "total" } },
                { kind: "Field", name: { kind: "Name", value: "undecided" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "question" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  PersonaCoveragesQuery,
  PersonaCoveragesQueryVariables
>;
export const PopulationsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Populations" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "populations" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "products" } },
                { kind: "Field", name: { kind: "Name", value: "answerRate" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PopulationsQuery, PopulationsQueryVariables>;
export const RecomputePersonaCoverageDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RecomputePersonaCoverage" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RecomputePersonaCoverageInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "recomputePersonaCoverage" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RecomputePersonaCoverageMutation,
  RecomputePersonaCoverageMutationVariables
>;
export const PersonasDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Personas" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "includeDerived" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Boolean" } },
          defaultValue: { kind: "BooleanValue", value: false },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "personas" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "includeDerived" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "includeDerived" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PersonaFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PersonasQuery, PersonasQueryVariables>;
export const PersonaDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Persona" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "persona" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PersonaFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PersonaQuery, PersonaQueryVariables>;
export const CreatePersonaDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CreatePersona" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "CreatePersonaInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "createPersona" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  CreatePersonaMutation,
  CreatePersonaMutationVariables
>;
export const UpdatePersonaDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdatePersona" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpdatePersonaInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updatePersona" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdatePersonaMutation,
  UpdatePersonaMutationVariables
>;
export const AddPersonaQuestionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "AddPersonaQuestion" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "AddPersonaQuestionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "addPersonaQuestion" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AddPersonaQuestionMutation,
  AddPersonaQuestionMutationVariables
>;
export const UpdatePersonaQuestionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdatePersonaQuestion" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpdatePersonaQuestionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updatePersonaQuestion" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdatePersonaQuestionMutation,
  UpdatePersonaQuestionMutationVariables
>;
export const RemovePersonaQuestionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RemovePersonaQuestion" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "RemovePersonaQuestionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "removePersonaQuestion" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RemovePersonaQuestionMutation,
  RemovePersonaQuestionMutationVariables
>;
export const DeletePersonaDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "DeletePersona" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "DeletePersonaInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "deletePersona" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "deletedPersonaId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  DeletePersonaMutation,
  DeletePersonaMutationVariables
>;
export const ClonePersonaFromDerivedDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ClonePersonaFromDerived" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "ClonePersonaInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "clonePersonaFromDerived" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "persona" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PersonaFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PersonaFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Persona" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "barriers" } },
          { kind: "Field", name: { kind: "Name", value: "origin" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "updatedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "order" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "fromTemplateId" },
                },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "brandContexts" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "BrandContextFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ClonePersonaFromDerivedMutation,
  ClonePersonaFromDerivedMutationVariables
>;
export const UpsertBrandContextDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpsertBrandContext" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpsertBrandContextInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "upsertBrandContext" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "brandContext" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "BrandContextFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "BrandContextFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "BrandContext" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "personaId" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpsertBrandContextMutation,
  UpsertBrandContextMutationVariables
>;
export const DeleteBrandContextDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "DeleteBrandContext" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "DeleteBrandContextInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "deleteBrandContext" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "deletedBrandContextId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  DeleteBrandContextMutation,
  DeleteBrandContextMutationVariables
>;
export const PostsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Posts" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "20" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "sourceType" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "SourceType" },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "query" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "hasMedia" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Boolean" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "posts" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "sourceType" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "sourceType" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "query" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "query" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "hasMedia" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "hasMedia" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PostFields" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PostFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Post" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "authorHash" } },
          { kind: "Field", name: { kind: "Name", value: "sourceType" } },
          { kind: "Field", name: { kind: "Name", value: "title" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "bodyWithheld" } },
          { kind: "Field", name: { kind: "Name", value: "mediaKind" } },
          { kind: "Field", name: { kind: "Name", value: "mediaUrl" } },
          { kind: "Field", name: { kind: "Name", value: "reactions" } },
          { kind: "Field", name: { kind: "Name", value: "postedAt" } },
          { kind: "Field", name: { kind: "Name", value: "isDisplayed" } },
          { kind: "Field", name: { kind: "Name", value: "tagCount" } },
          { kind: "Field", name: { kind: "Name", value: "productLinkCount" } },
          { kind: "Field", name: { kind: "Name", value: "videoCount" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PostsQuery, PostsQueryVariables>;
export const PostDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Post" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "post" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PostFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PostFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Post" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "authorHash" } },
          { kind: "Field", name: { kind: "Name", value: "sourceType" } },
          { kind: "Field", name: { kind: "Name", value: "title" } },
          { kind: "Field", name: { kind: "Name", value: "body" } },
          { kind: "Field", name: { kind: "Name", value: "bodyWithheld" } },
          { kind: "Field", name: { kind: "Name", value: "mediaKind" } },
          { kind: "Field", name: { kind: "Name", value: "mediaUrl" } },
          { kind: "Field", name: { kind: "Name", value: "reactions" } },
          { kind: "Field", name: { kind: "Name", value: "postedAt" } },
          { kind: "Field", name: { kind: "Name", value: "isDisplayed" } },
          { kind: "Field", name: { kind: "Name", value: "tagCount" } },
          { kind: "Field", name: { kind: "Name", value: "productLinkCount" } },
          { kind: "Field", name: { kind: "Name", value: "videoCount" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PostQuery, PostQueryVariables>;
export const ProductsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Products" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "20" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "filter" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "ProductFilter" },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "order" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "ProductOrder" },
          },
          defaultValue: { kind: "EnumValue", value: "NAME_ASC" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "DELIVERED" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "minUgcCount" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "filter" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "order" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "minUgcCount" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "minUgcCount" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "name" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "brand" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "category" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "sku" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "price" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "currency" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "imageUrl" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "enabled" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aio" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "coverage" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "faqTotal" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "faqDelivered",
                                    },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "axesFilled" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "axesTotal" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "publish" },
                                  },
                                ],
                              },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "answerability" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "population" },
                                  value: {
                                    kind: "Variable",
                                    name: { kind: "Name", value: "population" },
                                  },
                                },
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "covered" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "decided" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "faqCovered" },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductsQuery, ProductsQueryVariables>;
export const ProductDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Product" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "product" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "brand" } },
                { kind: "Field", name: { kind: "Name", value: "category" } },
                { kind: "Field", name: { kind: "Name", value: "audience" } },
                { kind: "Field", name: { kind: "Name", value: "sku" } },
                { kind: "Field", name: { kind: "Name", value: "price" } },
                { kind: "Field", name: { kind: "Name", value: "currency" } },
                { kind: "Field", name: { kind: "Name", value: "productUrl" } },
                { kind: "Field", name: { kind: "Name", value: "imageUrl" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "descriptionSources" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataCounts" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "reviews" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "staffPosts" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "socialPosts" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "videos" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "localAssets" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "idealContext" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "audience" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "concerns" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "scenes" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "value" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aio" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "coverage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "faqTotal" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "faqDelivered" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "axesFilled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "axesTotal" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "publish" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "tags" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "axis" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "values" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "source" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "faqs" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "ruleId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "question" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "answer" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "confidence" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "delivered" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "generatedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "generator" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "evidence" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerability" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "covered" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "decided" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "faqCovered" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "computedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "byPersona" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "personaId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "personaName" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "covered" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "decided" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "faqCovered" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "page" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "productId" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "body" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "jsonld" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "generatedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "promptVersions" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "faqCount" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "builder" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "dataGaps" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "external" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ProductQuery, ProductQueryVariables>;
export const PublishOverviewDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "PublishOverview" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "publishTargets" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "detail" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "available" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "unavailableReason" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "lastPublishedAt" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishScope" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "condition" } },
                { kind: "Field", name: { kind: "Name", value: "matched" } },
                { kind: "Field", name: { kind: "Name", value: "generated" } },
                { kind: "Field", name: { kind: "Name", value: "excluded" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "excludeReason" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "stoppedRules" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "basis" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "denominator" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "deliveryLog" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "at" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "target" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "products" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "result" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  PublishOverviewQuery,
  PublishOverviewQueryVariables
>;
export const WritebackPolicyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "WritebackPolicy" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "writebackPolicy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "version" } },
                { kind: "Field", name: { kind: "Name", value: "defaultMode" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "entries" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "target" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "mode" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "adapters" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "target" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "connected" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  WritebackPolicyQuery,
  WritebackPolicyQueryVariables
>;
export const WritebackProposalsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "WritebackProposals" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "filter" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "WritebackProposalFilter" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "writebackProposals" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "filter" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "WritebackProposalFields",
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "WritebackProposalFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "WritebackProposal" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "target" } },
          { kind: "Field", name: { kind: "Name", value: "field" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "proposedValue" } },
          { kind: "Field", name: { kind: "Name", value: "previousValue" } },
          { kind: "Field", name: { kind: "Name", value: "reason" } },
          { kind: "Field", name: { kind: "Name", value: "mode" } },
          { kind: "Field", name: { kind: "Name", value: "approvalSkipped" } },
          { kind: "Field", name: { kind: "Name", value: "proposedAt" } },
          { kind: "Field", name: { kind: "Name", value: "approvedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "approvedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "appliedAt" } },
          { kind: "Field", name: { kind: "Name", value: "revertedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  WritebackProposalsQuery,
  WritebackProposalsQueryVariables
>;
export const SetPublishTargetDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetPublishTarget" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetPublishTargetInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setPublishTarget" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "publishTarget" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "kind" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "available" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "lastPublishedAt" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetPublishTargetMutation,
  SetPublishTargetMutationVariables
>;
export const SetWritebackPolicyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetWritebackPolicy" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetWritebackPolicyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setWritebackPolicy" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "writebackPolicy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "defaultMode" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "entries" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "target" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "field" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "mode" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetWritebackPolicyMutation,
  SetWritebackPolicyMutationVariables
>;
export const ProposeProductPageWritebackDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ProposeProductPageWriteback" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "ProposeProductPageWritebackInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "proposeProductPageWriteback" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "proposal" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: {
                          kind: "Name",
                          value: "WritebackProposalFields",
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "WritebackProposalFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "WritebackProposal" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "target" } },
          { kind: "Field", name: { kind: "Name", value: "field" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "proposedValue" } },
          { kind: "Field", name: { kind: "Name", value: "previousValue" } },
          { kind: "Field", name: { kind: "Name", value: "reason" } },
          { kind: "Field", name: { kind: "Name", value: "mode" } },
          { kind: "Field", name: { kind: "Name", value: "approvalSkipped" } },
          { kind: "Field", name: { kind: "Name", value: "proposedAt" } },
          { kind: "Field", name: { kind: "Name", value: "approvedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "approvedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "appliedAt" } },
          { kind: "Field", name: { kind: "Name", value: "revertedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ProposeProductPageWritebackMutation,
  ProposeProductPageWritebackMutationVariables
>;
export const ApproveWritebackProposalDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ApproveWritebackProposal" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "WritebackProposalActionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "approveWritebackProposal" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "proposal" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: {
                          kind: "Name",
                          value: "WritebackProposalFields",
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "WritebackProposalFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "WritebackProposal" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "target" } },
          { kind: "Field", name: { kind: "Name", value: "field" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "proposedValue" } },
          { kind: "Field", name: { kind: "Name", value: "previousValue" } },
          { kind: "Field", name: { kind: "Name", value: "reason" } },
          { kind: "Field", name: { kind: "Name", value: "mode" } },
          { kind: "Field", name: { kind: "Name", value: "approvalSkipped" } },
          { kind: "Field", name: { kind: "Name", value: "proposedAt" } },
          { kind: "Field", name: { kind: "Name", value: "approvedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "approvedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "appliedAt" } },
          { kind: "Field", name: { kind: "Name", value: "revertedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ApproveWritebackProposalMutation,
  ApproveWritebackProposalMutationVariables
>;
export const RejectWritebackProposalDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "RejectWritebackProposal" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "WritebackProposalActionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "rejectWritebackProposal" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "proposal" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: {
                          kind: "Name",
                          value: "WritebackProposalFields",
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "WritebackProposalFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "WritebackProposal" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "target" } },
          { kind: "Field", name: { kind: "Name", value: "field" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "proposedValue" } },
          { kind: "Field", name: { kind: "Name", value: "previousValue" } },
          { kind: "Field", name: { kind: "Name", value: "reason" } },
          { kind: "Field", name: { kind: "Name", value: "mode" } },
          { kind: "Field", name: { kind: "Name", value: "approvalSkipped" } },
          { kind: "Field", name: { kind: "Name", value: "proposedAt" } },
          { kind: "Field", name: { kind: "Name", value: "approvedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "approvedBy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "displayName" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "appliedAt" } },
          { kind: "Field", name: { kind: "Name", value: "revertedAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RejectWritebackProposalMutation,
  RejectWritebackProposalMutationVariables
>;
export const QuestionMapDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "QuestionMap" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "population" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "PopulationId" },
          },
          defaultValue: { kind: "EnumValue", value: "DELIVERED" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "questionTemplates" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "origin" } },
                { kind: "Field", name: { kind: "Name", value: "ruleId" } },
                { kind: "Field", name: { kind: "Name", value: "ruleState" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                { kind: "Field", name: { kind: "Name", value: "answerCount" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "questionCoverage" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "population" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "population" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "question" } },
                { kind: "Field", name: { kind: "Name", value: "coverage" } },
                { kind: "Field", name: { kind: "Name", value: "available" } },
                { kind: "Field", name: { kind: "Name", value: "missing" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "basis" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "note" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "denominator" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "rules" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "name" } },
                { kind: "Field", name: { kind: "Name", value: "state" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<QuestionMapQuery, QuestionMapQueryVariables>;
export const AddQuestionTemplateDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "AddQuestionTemplate" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "AddQuestionTemplateInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "addQuestionTemplate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "template" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "scope" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "ruleId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  AddQuestionTemplateMutation,
  AddQuestionTemplateMutationVariables
>;
export const SetQuestionTemplateEnabledDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetQuestionTemplateEnabled" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetQuestionTemplateEnabledInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setQuestionTemplateEnabled" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "template" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetQuestionTemplateEnabledMutation,
  SetQuestionTemplateEnabledMutationVariables
>;
export const RulesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Rules" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "rules" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "RuleSummaryFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RulesQuery, RulesQueryVariables>;
export const RuleDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Rule" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "rule" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "RuleDetailFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RuleQuery, RuleQueryVariables>;
export const CreateRuleDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "CreateRule" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "CreateRuleInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "createRule" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rule" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RuleDetailFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateRuleMutation, CreateRuleMutationVariables>;
export const UpdateRuleDraftDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateRuleDraft" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpdateRuleDraftInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateRuleDraft" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rule" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RuleDetailFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdateRuleDraftMutation,
  UpdateRuleDraftMutationVariables
>;
export const SaveRuleVersionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SaveRuleVersion" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SaveRuleVersionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "saveRuleVersion" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rule" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RuleDetailFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "promptVersion" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "savedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "isPublished" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SaveRuleVersionMutation,
  SaveRuleVersionMutationVariables
>;
export const SetPublishedPromptVersionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetPublishedPromptVersion" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetPublishedPromptVersionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setPublishedPromptVersion" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rule" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RuleDetailFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetPublishedPromptVersionMutation,
  SetPublishedPromptVersionMutationVariables
>;
export const SetRuleStateDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetRuleState" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetRuleStateInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setRuleState" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "rule" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RuleDetailFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleSummaryFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "name" } },
          { kind: "Field", name: { kind: "Name", value: "description" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "scope" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "publishedPromptVersion" },
          },
          { kind: "Field", name: { kind: "Name", value: "maxPerProduct" } },
          { kind: "Field", name: { kind: "Name", value: "minEvidenceCount" } },
          { kind: "Field", name: { kind: "Name", value: "answerMaxChars" } },
          { kind: "Field", name: { kind: "Name", value: "questions" } },
          { kind: "Field", name: { kind: "Name", value: "banned" } },
          { kind: "Field", name: { kind: "Name", value: "ruleVersion" } },
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "lastRunAt" } },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RuleDetailFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Rule" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RuleSummaryFields" },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "draft" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "promptVersion" },
                },
                { kind: "Field", name: { kind: "Name", value: "promptBody" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "maxPerProduct" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minEvidenceCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answerMaxChars" },
                },
                { kind: "Field", name: { kind: "Name", value: "banned" } },
                { kind: "Field", name: { kind: "Name", value: "scope" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "promptVersions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: { kind: "IntValue", value: "20" },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptVersion" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "promptBody" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "isPublished" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "savedBy" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" },
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "displayName",
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetRuleStateMutation,
  SetRuleStateMutationVariables
>;
export const DryRunRuleDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "DryRunRule" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "DryRunRuleInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "dryRunRule" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "result" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "DryRunFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "DryRunResult" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "job" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "DryRunJobFields" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "comparison" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answers" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "productId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "before" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "beforeNote" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "after" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "afterNote" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "changed" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DryRunRuleMutation, DryRunRuleMutationVariables>;
export const DryRunDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "DryRun" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "jobId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "dryRun" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "jobId" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "jobId" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "DryRunFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "DryRunResult" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "job" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "DryRunJobFields" },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "comparison" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "generatedAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsBefore" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "bannedHitsAfter" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "answers" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "productId" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "question" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "before" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "beforeNote" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "after" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "afterNote" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "changed" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DryRunQuery, DryRunQueryVariables>;
export const RuleJobProgressDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "RuleJobProgress" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "job" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "DryRunJobFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RuleJobProgressQuery,
  RuleJobProgressQueryVariables
>;
export const TenantRunningJobsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "TenantRunningJobs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "kind" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "JobKind" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "state" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "JobState" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "5" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "jobs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "kind" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "kind" },
                      },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "state" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "state" },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "DryRunJobFields" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DryRunJobFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "JobProgressFields" },
          },
          { kind: "Field", name: { kind: "Name", value: "droppedFaqs" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "droppedFaqReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  TenantRunningJobsQuery,
  TenantRunningJobsQueryVariables
>;
export const RunStopPolicyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "RunStopPolicy" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "runStopPolicy" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "value" } },
                { kind: "Field", name: { kind: "Name", value: "version" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "editableByViewer" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RunStopPolicyQuery, RunStopPolicyQueryVariables>;
export const SetRunStopPolicyDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetRunStopPolicy" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetRunStopPolicyInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setRunStopPolicy" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "runStopPolicy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "value" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "updatedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "updatedBy" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "displayName" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "editableByViewer" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetRunStopPolicyMutation,
  SetRunStopPolicyMutationVariables
>;
export const SellingAnglesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "SellingAngles" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "productId" },
          },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "sellingAngles" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "productId" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "productId" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "SellingAngleFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "SellingAngleFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "SellingAngle" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "key" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "cut" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "attributeSource" } },
          { kind: "Field", name: { kind: "Name", value: "idea" } },
          { kind: "Field", name: { kind: "Name", value: "adoption" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "dataGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SellingAnglesQuery, SellingAnglesQueryVariables>;
export const SellingProductChoicesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "SellingProductChoices" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "20" },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "filter" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "ProductFilter" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "filter" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "name" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SellingProductChoicesQuery,
  SellingProductChoicesQueryVariables
>;
export const SetSellingAngleAdoptionDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetSellingAngleAdoption" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetSellingAngleAdoptionInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setSellingAngleAdoption" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "angle" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "SellingAngleFields" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "SellingAngleFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "SellingAngle" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "key" } },
          { kind: "Field", name: { kind: "Name", value: "productId" } },
          { kind: "Field", name: { kind: "Name", value: "cut" } },
          { kind: "Field", name: { kind: "Name", value: "attributes" } },
          { kind: "Field", name: { kind: "Name", value: "attributeSource" } },
          { kind: "Field", name: { kind: "Name", value: "idea" } },
          { kind: "Field", name: { kind: "Name", value: "adoption" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "dataGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetSellingAngleAdoptionMutation,
  SetSellingAngleAdoptionMutationVariables
>;
export const RegulationBookDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "RegulationBook" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "regulationBook" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "RegulationBookFields" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RegulationBookFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "RegulationBook" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "tones" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "bannedPhrases" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "wordingRules" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "use" } },
                { kind: "Field", name: { kind: "Name", value: "avoid" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RegulationBookQuery, RegulationBookQueryVariables>;
export const UpdateRegulationBookDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UpdateRegulationBook" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UpdateRegulationBookInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "updateRegulationBook" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "regulationBook" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "RegulationBookFields" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RegulationBookFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "RegulationBook" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "version" } },
          { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "tones" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "bannedPhrases" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "text" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "wordingRules" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "use" } },
                { kind: "Field", name: { kind: "Name", value: "avoid" } },
                { kind: "Field", name: { kind: "Name", value: "addedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "addedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdateRegulationBookMutation,
  UpdateRegulationBookMutationVariables
>;
export const VideoMetaAutoIngestDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "VideoMetaAutoIngest" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "videoMetaAutoIngest" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "enabled" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "monthlyLimit" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "processedThisMonth" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "suspendedByLimit" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "lastDetectedAt" },
                },
                { kind: "Field", name: { kind: "Name", value: "version" } },
                { kind: "Field", name: { kind: "Name", value: "updatedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "updatedBy" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "editableByViewer" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  VideoMetaAutoIngestQuery,
  VideoMetaAutoIngestQueryVariables
>;
export const SetVideoMetaAutoIngestDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "SetVideoMetaAutoIngest" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "SetVideoMetaAutoIngestInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "setVideoMetaAutoIngest" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "autoIngest" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "enabled" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "monthlyLimit" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "processedThisMonth" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "suspendedByLimit" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "lastDetectedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "version" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "updatedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "updatedBy" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "displayName" },
                            },
                          ],
                        },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "editableByViewer" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  SetVideoMetaAutoIngestMutation,
  SetVideoMetaAutoIngestMutationVariables
>;
export const VideoMetaDocsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "VideoMetaDocs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "after" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Cursor" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "filter" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "VideoMetaFilter" },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "order" },
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "VideoMetaOrder" },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "videoMetaDocs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "after" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "after" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "filter" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "order" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "pageInfo" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "hasNextPage" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endCursor" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "ugcId" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "seq" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "videoUrl" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "hlsUrl" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "thumbnailUrl" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "durationSec" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "width" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "height" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "postedAt" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "processState" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "skipReason" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "processNote" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "linkState" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "audioState" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "processGap" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "what" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "why" },
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "needs" },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<VideoMetaDocsQuery, VideoMetaDocsQueryVariables>;
export const VideoMetaBatchEstimateDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "VideoMetaBatchEstimate" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "videoMetaBatchEstimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "processedCount" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "processedCountExact" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  VideoMetaBatchEstimateQuery,
  VideoMetaBatchEstimateQueryVariables
>;
export const StartVideoMetaBatchDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "StartVideoMetaBatch" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "StartVideoMetaBatchInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "startVideoMetaBatch" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  StartVideoMetaBatchMutation,
  StartVideoMetaBatchMutationVariables
>;
export const RunningVideoMetaJobsDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "RunningVideoMetaJobs" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "state" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "JobState" },
            },
          },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "5" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "jobs" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "kind" },
                      value: { kind: "EnumValue", value: "VIDEO_META" },
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "state" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "state" },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "JobProgressFields",
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  RunningVideoMetaJobsQuery,
  RunningVideoMetaJobsQueryVariables
>;
export const VideoMetaDocDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "VideoMetaDoc" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: { kind: "NamedType", name: { kind: "Name", value: "ID" } },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "videoMetaDoc" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "ugcId" } },
                { kind: "Field", name: { kind: "Name", value: "seq" } },
                { kind: "Field", name: { kind: "Name", value: "videoUrl" } },
                { kind: "Field", name: { kind: "Name", value: "hlsUrl" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thumbnailUrl" },
                },
                { kind: "Field", name: { kind: "Name", value: "durationSec" } },
                { kind: "Field", name: { kind: "Name", value: "width" } },
                { kind: "Field", name: { kind: "Name", value: "height" } },
                { kind: "Field", name: { kind: "Name", value: "postedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "processState" },
                },
                { kind: "Field", name: { kind: "Name", value: "skipReason" } },
                { kind: "Field", name: { kind: "Name", value: "processNote" } },
                { kind: "Field", name: { kind: "Name", value: "sourceType" } },
                { kind: "Field", name: { kind: "Name", value: "hasAudio" } },
                { kind: "Field", name: { kind: "Name", value: "summary" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "mainProducts" },
                },
                { kind: "Field", name: { kind: "Name", value: "mainTopics" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "peopleSummary" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "audioSummary" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "frames" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "index" } },
                      { kind: "Field", name: { kind: "Name", value: "atSec" } },
                      { kind: "Field", name: { kind: "Name", value: "url" } },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "linkState" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "minConfidence" },
                },
                { kind: "Field", name: { kind: "Name", value: "audioState" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "hasOnScreenText" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "interpreterId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "interpretedAt" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "processGap" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "what" } },
                      { kind: "Field", name: { kind: "Name", value: "why" } },
                      { kind: "Field", name: { kind: "Name", value: "needs" } },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "segments" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "VideoSegmentFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "transcript" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "index" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "startSec" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "endSec" },
                      },
                      { kind: "Field", name: { kind: "Name", value: "text" } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VideoSegmentFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VideoSegment" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "index" } },
          { kind: "Field", name: { kind: "Name", value: "startSec" } },
          { kind: "Field", name: { kind: "Name", value: "endSec" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "people" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "roleGuess" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "topics" } },
          { kind: "Field", name: { kind: "Name", value: "onScreenText" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "evidence" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "index" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "basis" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "vocabTags" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "confirmation" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "productId" } },
                { kind: "Field", name: { kind: "Name", value: "productName" } },
                { kind: "Field", name: { kind: "Name", value: "confirmedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "confirmedByUserId" },
                },
                { kind: "Field", name: { kind: "Name", value: "version" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<VideoMetaDocQuery, VideoMetaDocQueryVariables>;
export const ConfirmSegmentProductDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "ConfirmSegmentProduct" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "ConfirmSegmentProductInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "confirmSegmentProduct" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "segment" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "VideoSegmentFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VideoSegmentFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VideoSegment" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "index" } },
          { kind: "Field", name: { kind: "Name", value: "startSec" } },
          { kind: "Field", name: { kind: "Name", value: "endSec" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "people" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "roleGuess" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "topics" } },
          { kind: "Field", name: { kind: "Name", value: "onScreenText" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "evidence" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "index" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "basis" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "vocabTags" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "confirmation" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "productId" } },
                { kind: "Field", name: { kind: "Name", value: "productName" } },
                { kind: "Field", name: { kind: "Name", value: "confirmedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "confirmedByUserId" },
                },
                { kind: "Field", name: { kind: "Name", value: "version" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ConfirmSegmentProductMutation,
  ConfirmSegmentProductMutationVariables
>;
export const UnconfirmSegmentProductDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "UnconfirmSegmentProduct" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "UnconfirmSegmentProductInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "unconfirmSegmentProduct" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "segment" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "VideoSegmentFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VideoSegmentFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "VideoSegment" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "index" } },
          { kind: "Field", name: { kind: "Name", value: "startSec" } },
          { kind: "Field", name: { kind: "Name", value: "endSec" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "people" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "roleGuess" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
                { kind: "Field", name: { kind: "Name", value: "description" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "topics" } },
          { kind: "Field", name: { kind: "Name", value: "onScreenText" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "evidence" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "kind" } },
                { kind: "Field", name: { kind: "Name", value: "index" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "basis" } },
                { kind: "Field", name: { kind: "Name", value: "confidence" } },
                { kind: "Field", name: { kind: "Name", value: "vocabTags" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "confirmation" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "productId" } },
                { kind: "Field", name: { kind: "Name", value: "productName" } },
                { kind: "Field", name: { kind: "Name", value: "confirmedAt" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "confirmedByUserId" },
                },
                { kind: "Field", name: { kind: "Name", value: "version" } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UnconfirmSegmentProductMutation,
  UnconfirmSegmentProductMutationVariables
>;
export const VideoSegmentProductChoicesDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "VideoSegmentProductChoices" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "query" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } },
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "first" },
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } },
          defaultValue: { kind: "IntValue", value: "10" },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "products" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "first" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "first" },
                },
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "filter" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "query" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "query" },
                      },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "totalCount" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "edges" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "cursor" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "node" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "name" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "brand" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  VideoSegmentProductChoicesQuery,
  VideoSegmentProductChoicesQueryVariables
>;
export const EnqueueVideoMetaJobDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "EnqueueVideoMetaJob" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "input" },
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "EnqueueVideoMetaJobInput" },
            },
          },
        },
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "enqueueVideoMetaJob" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "input" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "input" },
                },
              },
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "job" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "JobProgressFields" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "auditEventId" },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "userErrors" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "field" } },
                      { kind: "Field", name: { kind: "Name", value: "code" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "message" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobEstimateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "JobEstimate" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "targetCount" } },
          { kind: "Field", name: { kind: "Name", value: "skippedCount" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "estimatedCost" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedCurrency" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedSeconds" } },
          { kind: "Field", name: { kind: "Name", value: "estimatedLlmCalls" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "basis" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "note" } },
                { kind: "Field", name: { kind: "Name", value: "denominator" } },
              ],
            },
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "costGap" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "what" } },
                { kind: "Field", name: { kind: "Name", value: "why" } },
                { kind: "Field", name: { kind: "Name", value: "needs" } },
                { kind: "Field", name: { kind: "Name", value: "external" } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "JobProgressFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "Job" },
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "kind" } },
          { kind: "Field", name: { kind: "Name", value: "state" } },
          { kind: "Field", name: { kind: "Name", value: "progress" } },
          { kind: "Field", name: { kind: "Name", value: "total" } },
          { kind: "Field", name: { kind: "Name", value: "succeeded" } },
          { kind: "Field", name: { kind: "Name", value: "failed" } },
          { kind: "Field", name: { kind: "Name", value: "skipped" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "skippedReasons" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "reason" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "count" } },
              ],
            },
          },
          { kind: "Field", name: { kind: "Name", value: "deferred" } },
          { kind: "Field", name: { kind: "Name", value: "startedAt" } },
          { kind: "Field", name: { kind: "Name", value: "finishedAt" } },
          { kind: "Field", name: { kind: "Name", value: "enqueuedAt" } },
          { kind: "Field", name: { kind: "Name", value: "errorSummary" } },
          { kind: "Field", name: { kind: "Name", value: "retryable" } },
          { kind: "Field", name: { kind: "Name", value: "retriedByJobId" } },
          { kind: "Field", name: { kind: "Name", value: "retryOfJobId" } },
          { kind: "Field", name: { kind: "Name", value: "cancelRequestedAt" } },
          { kind: "Field", name: { kind: "Name", value: "cancelEffect" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "suggestedPollIntervalSec" },
          },
          { kind: "Field", name: { kind: "Name", value: "auditEventId" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "estimate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "JobEstimateFields" },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  EnqueueVideoMetaJobMutation,
  EnqueueVideoMetaJobMutationVariables
>;
export const ViewerDocument = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "Viewer" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "viewer" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "user" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "displayName" },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "tenant" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                    ],
                  },
                },
                { kind: "Field", name: { kind: "Name", value: "role" } },
                { kind: "Field", name: { kind: "Name", value: "permissions" } },
                { kind: "Field", name: { kind: "Name", value: "isOperator" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "memberships" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "role" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "permissions" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "tenant" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" },
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "createdAt" },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "delegation" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "reason" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "reasonNote" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "startedAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "expiresAt" },
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "actorLabel" },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ViewerQuery, ViewerQueryVariables>;
